import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { exportElementPDF, exportElementPNG } from '../../utils/pdfExport'
import {
  Users, Search, Download, CheckCircle2, XCircle, ArrowLeft,
  Plus, Edit, BarChart3, Calendar, AlertCircle, AlertTriangle,
  RefreshCw, ChevronDown, ChevronUp, Mic, FileText,
  TrendingUp, ClipboardList, Mail, Clock, Copy, Link as LinkIcon, Check, Award,
  ExternalLink, LogIn, ShieldCheck, Eye, Upload, Trash2, RotateCcw,
  BookOpen, Sparkles, FileDown, X,
  Wallet, ArrowDownCircle, ArrowUpCircle, Pencil, DollarSign
} from 'lucide-react'
import toast from 'react-hot-toast'
import { masterclassService } from '../../services/masterclassService'
import { useAuthStore } from '../../store/authStore'
import { SCHEDULE_LABEL, SCHEDULE_SERIES_NAME, getSuggestedCloneDate } from '../../utils/masterclassSchedule'
import { resolveImageUrl, getSpeakerObjectPosition } from '../../utils/imageUrl'
import * as flyerConstants from '../../utils/flyerConstants'
import { MASTERCLASS_TOPIC_CATEGORIES, MASTERCLASS_TOPIC_LIBRARY } from '../../data/masterclassTopicLibrary'
import MasterclassMediaManager from './MasterclassMediaManager'
import DigitalEventPass from '../../components/masterclass/DigitalEventPass'
import LoadingSpinner from '../../components/LoadingSpinner'

function ExportCSV(rows) {
  if (!rows.length) return
  const headers = Object.keys(rows[0])
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => JSON.stringify(r[h] ?? '')).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `masterclass-registrations-${Date.now()}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

function TabBtn({ active, onClick, children, icon: Icon, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 font-sans text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
        active
          ? 'cinema-btn-primary text-white shadow-lg shadow-cinema-glow/10'
          : 'cinema-btn-ghost text-cinema-muted hover:text-white'
      }`}
    >
      {Icon && <Icon className="w-4 h-4 shrink-0" />}
      <span>{children}</span>
      {badge > 0 && (
        <span className={`ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${
          active ? 'bg-white text-black' : 'bg-cinema-glow/20 text-cinema-glow border border-cinema-glow/30'
        }`}>
          {badge}
        </span>
      )}
    </button>
  )
}

function SubTabBtn({ active, onClick, children, icon: Icon, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 font-sans text-xs font-medium transition-all border ${
        active
          ? 'bg-white/10 text-white border-cinema-glow/40 shadow-sm'
          : 'bg-transparent text-cinema-muted hover:text-white border-white/5 hover:border-white/15'
      }`}
    >
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
      <span>{children}</span>
      {badge > 0 && (
        <span className="ml-1 px-1.5 py-0.2 text-[9px] font-bold bg-cinema-glow/20 text-cinema-glow border border-cinema-glow/30">
          {badge}
        </span>
      )}
    </button>
  )
}

function EmptyState({ icon: Icon, title, description, actionText, onAction, actionIcon: ActionIcon = Plus }) {
  return (
    <div className="text-center py-12 px-4 border border-white/5 bg-white/[0.01] my-4 max-w-lg mx-auto">
      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-cinema-glow/10 text-cinema-glow">
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="text-base font-semibold text-white font-sans">{title}</h3>
      <p className="text-xs text-cinema-muted font-sans mt-1.5 leading-relaxed max-w-sm mx-auto">
        {description}
      </p>
      {actionText && onAction && (
        <div className="mt-5">
          <button
            type="button"
            onClick={onAction}
            className="cinema-btn-primary !px-4 !py-2 !min-h-0 text-xs inline-flex items-center gap-1.5 mx-auto"
          >
            <ActionIcon className="w-3.5 h-3.5" />
            {actionText}
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Scheduling rule helpers: one masterclass per month, on the last Friday ──
// Pure functions — kept at module scope so they resolve cleanly for both
// `no-undef` and `no-unused-vars`, and can be reused by any nested render.
function getLastFridayOfMonth(year, month) {
  const lastDay = new Date(year, month + 1, 0).getDate()
  for (let d = lastDay; d > 0; d--) {
    const date = new Date(year, month, d)
    if (date.getDay() === 5) return date
  }
  return null
}

function isLastFridayOfMonth(dateStr) {
  if (!dateStr) return false
  const d = new Date(dateStr)
  if (Number.isNaN(d.getTime())) return false
  const lastFriday = getLastFridayOfMonth(d.getFullYear(), d.getMonth())
  if (!lastFriday) return false
  return d.getDate() === lastFriday.getDate() &&
         d.getMonth() === lastFriday.getMonth() &&
         d.getFullYear() === lastFriday.getFullYear()
}

function getNextLastFriday() {
  const now = new Date()
  let year = now.getFullYear()
  let month = now.getMonth()
  // If we're past this month's last Friday, look at next month
  const thisMonthLastFriday = getLastFridayOfMonth(year, month)
  if (thisMonthLastFriday && now > thisMonthLastFriday) {
    month++
    if (month > 11) { month = 0; year++ }
  }
  const lastFriday = getLastFridayOfMonth(year, month)
  return lastFriday ? lastFriday.toISOString().split('T')[0] : ''
}

function formatDateLong(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-UG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

export default function MasterclassAdminPage() {
  const { user, isAuthenticated } = useAuthStore()
  const isAuthorized = isAuthenticated && (user?.user_type === 'admin' || user?.user_type === 'finance' || user?.user_type === 'trainer')
  const canVerifyPayments = isAuthenticated && (user?.user_type === 'admin' || user?.user_type === 'finance')

  // 5 Primary Pillars: overview, topics, media, registrations, operations
  const [mainTab, setMainTab] = useState('overview')
  const [topicsSubTab, setTopicsSubTab] = useState('topics') // 'topics' | 'speakers'
  const [regSubTab, setRegSubTab] = useState('registrations') // 'registrations' | 'verifications'
  const [opsSubTab, setOpsSubTab] = useState('checkin') // 'checkin' | 'attendance' | 'attcerts' | 'reminders' | 'analytics'
  const [certSubTab] = useState('single') // deprecated — only single-session certs now
  const [pendingVerifCount, setPendingVerifCount] = useState(0)
  const [selectedRegistrationMC, setSelectedRegistrationMC] = useState(null)
  const [certSearchSeed, setCertSearchSeed] = useState('')

  // Fetch pending verifications count for badge
  useEffect(() => {
    if (canVerifyPayments) {
      masterclassService.adminGetPendingVerifications({ status: 'PENDING_VERIFICATION' })
        .then(d => setPendingVerifCount((d.verifications || []).length))
        .catch(() => {})
    }
  }, [canVerifyPayments, mainTab, regSubTab])

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 cinema-page bg-cinema-black">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-cinema-glow/10">
            <LogIn className="w-8 h-8 text-cinema-glow" />
          </div>
          <h1 className="font-display text-white text-2xl font-bold">Staff Access Required</h1>
          <p className="text-cinema-muted font-sans text-sm mt-3">
            Log in with an authorized account to manage masterclass sessions, registrations, and media.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/login" className="cinema-btn-primary justify-center text-sm">Log In</Link>
            <Link to="/admin" className="cinema-btn-ghost justify-center text-sm">Dashboard</Link>
          </div>
          <div className="mt-6">
            <Link to="/explore" className="text-cinema-muted hover:text-cinema-gold font-sans text-sm transition-colors">← Back to website</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen cinema-page bg-cinema-black text-white">
      <style>{`
        .table-row:hover { background: rgba(255,255,255,0.03); }
        .cinema-table-head { border-bottom: 1px solid rgba(255,255,255,0.07); }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Header */}
      <div className="sticky top-0 z-40 cinema-glass-strong border-b border-white/10">
        <div className="w-full px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/explore" className="text-cinema-muted hover:text-white transition-colors" title="Back to Explore">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <span className="cinema-label text-[10px] tracking-widest text-cinema-glow uppercase">Event Management</span>
              <h1 className="font-display text-white font-bold text-lg leading-tight">{SCHEDULE_SERIES_NAME}</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/masterclass"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-cinema-muted hover:text-white border border-white/10 px-3 py-1.5 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View Public Page
            </Link>
            <Link to="/admin" className="text-cinema-muted hover:text-white font-sans text-xs sm:text-sm transition-colors">
              Admin Home
            </Link>
          </div>
        </div>
      </div>

      {/* 5 Primary Navigation Pillars */}
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar sm:flex-wrap border-b border-white/10 pb-4">
          <TabBtn
            active={mainTab === 'overview'}
            onClick={() => setMainTab('overview')}
            icon={BarChart3}
          >
            Overview
          </TabBtn>
          <TabBtn
            active={mainTab === 'topics'}
            onClick={() => setMainTab('topics')}
            icon={Edit}
          >
            Topics & Speakers
          </TabBtn>
          <TabBtn
            active={mainTab === 'media'}
            onClick={() => setMainTab('media')}
            icon={FileText}
          >
            Media & Resources
          </TabBtn>
          <TabBtn
            active={mainTab === 'registrations'}
            onClick={() => setMainTab('registrations')}
            icon={Users}
            badge={pendingVerifCount}
          >
            Registrations & Payments
          </TabBtn>
          <TabBtn
            active={mainTab === 'operations'}
            onClick={() => setMainTab('operations')}
            icon={ClipboardList}
          >
            Event Operations
          </TabBtn>
          <TabBtn
            active={mainTab === 'funds'}
            onClick={() => setMainTab('funds')}
            icon={Wallet}
          >
            Funds
          </TabBtn>
        </div>

        {/* Tab 1: Overview Dashboard */}
        {mainTab === 'overview' && (
          <DashboardTab
            onNavigate={(targetTab, subTab) => {
              setMainTab(targetTab)
              if (targetTab === 'topics' && subTab) setTopicsSubTab(subTab)
              if (targetTab === 'registrations' && subTab) setRegSubTab(subTab)
              if (targetTab === 'operations' && subTab) setOpsSubTab(subTab)
            }}
          />
        )}

        {/* Tab 2: Topics & Speakers */}
        {mainTab === 'topics' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-3">
              <div className="flex gap-2">
                <SubTabBtn
                  active={topicsSubTab === 'topics'}
                  onClick={() => setTopicsSubTab('topics')}
                  icon={Edit}
                >
                  Topics & Programme
                </SubTabBtn>
                <SubTabBtn
                  active={topicsSubTab === 'speakers'}
                  onClick={() => setTopicsSubTab('speakers')}
                  icon={Mic}
                >
                  Speakers
                </SubTabBtn>
              </div>
            </div>

            {topicsSubTab === 'topics' && (
              <TopicsProgrammeView
                onNavigateRegistrations={(mcId) => {
                  setSelectedRegistrationMC(mcId)
                  setMainTab('registrations')
                  setRegSubTab('registrations')
                }}
              />
            )}
            {topicsSubTab === 'speakers' && <SpeakersView />}
          </div>
        )}

        {/* Tab 3: Media & Resources */}
        {mainTab === 'media' && <MasterclassMediaManager />}

        {/* Tab 4: Registrations & Payments */}
        {mainTab === 'registrations' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-3">
              <div className="flex gap-2">
                <SubTabBtn
                  active={regSubTab === 'registrations'}
                  onClick={() => setRegSubTab('registrations')}
                  icon={Users}
                >
                  All Registrations
                </SubTabBtn>
                {canVerifyPayments && (
                  <SubTabBtn
                    active={regSubTab === 'verifications'}
                    onClick={() => setRegSubTab('verifications')}
                    icon={ShieldCheck}
                    badge={pendingVerifCount}
                  >
                    Payment Verifications
                  </SubTabBtn>
                )}
              </div>
            </div>

            {regSubTab === 'registrations' && (
              <RegistrationsTab
                onNavigateVerifications={() => setRegSubTab('verifications')}
                onNavigateCertificates={(email) => {
                  setCertSearchSeed(email)
                  setMainTab('operations')
                  setOpsSubTab('attcerts')
                }}
                initialMasterclassId={selectedRegistrationMC}
              />
            )}
            {regSubTab === 'verifications' && canVerifyPayments && (
              <PaymentVerificationsTab />
            )}
          </div>
        )}

        {/* Tab 5: Event Operations */}
        {mainTab === 'operations' && (
          <div className="space-y-6">
            <div className="flex gap-2 overflow-x-auto no-scrollbar sm:flex-wrap border-b border-white/10 pb-3">
              <SubTabBtn
                active={opsSubTab === 'checkin'}
                onClick={() => setOpsSubTab('checkin')}
                icon={CheckCircle2}
              >
                Door Check-In
              </SubTabBtn>
              <SubTabBtn
                active={opsSubTab === 'attendance'}
                onClick={() => setOpsSubTab('attendance')}
                icon={ClipboardList}
              >
                Attendance Report
              </SubTabBtn>
              <SubTabBtn
                active={opsSubTab === 'attcerts'}
                onClick={() => setOpsSubTab('attcerts')}
                icon={Award}
              >
                Certificates
              </SubTabBtn>
              <SubTabBtn
                active={opsSubTab === 'reminders'}
                onClick={() => setOpsSubTab('reminders')}
                icon={Mail}
              >
                Email Reminders
              </SubTabBtn>
              <SubTabBtn
                active={opsSubTab === 'analytics'}
                onClick={() => setOpsSubTab('analytics')}
                icon={TrendingUp}
              >
                Analytics
              </SubTabBtn>
            </div>

            {opsSubTab === 'checkin' && <CheckInTab />}
            {opsSubTab === 'attendance' && <AttendanceReportTab />}
            {opsSubTab === 'attcerts' && (
              <SingleSessionCertificatesTab
                initialSearch={certSearchSeed}
                onSearchConsumed={() => setCertSearchSeed('')}
              />
            )}
            {opsSubTab === 'reminders' && <RemindersTab />}
            {opsSubTab === 'analytics' && <AnalyticsTab />}
          </div>
        )}

        {/* Tab 6: Funds */}
        {mainTab === 'funds' && <FundsTab />}
      </div>
    </div>
  )
}

// ── Dashboard (Overview & Needs Attention) ────────────────────────────────────
function DashboardTab({ onNavigate }) {
  const [stats, setStats] = useState(null)
  const [masterclasses, setMasterclasses] = useState([])
  const [speakers, setSpeakers] = useState([])
  const [pendingVerifications, setPendingVerifications] = useState([])
  const [selectedMC, setSelectedMC] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      masterclassService.adminGetStats(),
      masterclassService.adminGetMasterclasses(),
      masterclassService.getSpeakers(),
      masterclassService.adminGetPendingVerifications({ status: 'PENDING_VERIFICATION' }).catch(() => ({ verifications: [] })),
    ]).then(([s, m, spk, v]) => {
      setStats(s.stats)
      setMasterclasses(m.masterclasses || [])
      setSpeakers(spk.speakers || [])
      setPendingVerifications(v.verifications || [])
    }).catch(console.error).finally(() => setLoading(false))
  }, [])

  const loadStats = async (mcId) => {
    setSelectedMC(mcId)
    const s = await masterclassService.adminGetStats(mcId || undefined).catch(() => null)
    if (s) setStats(s.stats)
  }

  if (loading) return <LoadingSpinner className="py-16" />

  const activeUpcomingSessions = masterclasses.filter(m => {
    const status = (m.status || 'upcoming').toLowerCase()
    return !['completed', 'cancelled', 'draft'].includes(status) && !m.is_archived && m.is_active !== false
  })
  const upcomingMC = activeUpcomingSessions.find(m => new Date(m.event_date) >= new Date()) || activeUpcomingSessions[0] || null

  // Compute "Needs Attention" actionable items
  const attentionItems = []

  if (pendingVerifications.length > 0) {
    attentionItems.push({
      id: 'verif',
      icon: ShieldCheck,
      color: 'text-amber-400',
      bgColor: 'bg-amber-500/10 border-amber-500/30',
      title: `${pendingVerifications.length} Guest Payment${pendingVerifications.length > 1 ? 's' : ''} Awaiting Review`,
      description: 'Submitted payment screenshots need verification to confirm guest passes and issue receipts.',
      actionText: 'Review Payments',
      onClick: () => onNavigate('registrations', 'verifications'),
    })
  }

  const unassignedSessions = masterclasses.filter(m => !m.speaker_id)
  if (unassignedSessions.length > 0) {
    attentionItems.push({
      id: 'speaker_missing',
      icon: AlertTriangle,
      color: 'text-rose-400',
      bgColor: 'bg-rose-500/10 border-rose-500/30',
      title: `${unassignedSessions.length} Session${unassignedSessions.length > 1 ? 's' : ''} Missing Assigned Speaker`,
      description: `"${unassignedSessions[0].topic}" has no speaker attached to teach the session.`,
      actionText: 'Assign Speaker',
      onClick: () => onNavigate('topics', 'topics'),
    })
  }

  const speakersWithoutPhotos = speakers.filter(s => !s.photo_url)
  if (speakersWithoutPhotos.length > 0) {
    attentionItems.push({
      id: 'photo_missing',
      icon: Upload,
      color: 'text-sky-400',
      bgColor: 'bg-sky-500/10 border-sky-500/30',
      title: `${speakersWithoutPhotos.length} Speaker Profile${speakersWithoutPhotos.length > 1 ? 's' : ''} Missing Photo`,
      description: `${speakersWithoutPhotos[0].name} has no portrait photo uploaded for public display.`,
      actionText: 'Upload Photo',
      onClick: () => onNavigate('topics', 'speakers'),
    })
  }

  const sessionsMissingDescription = masterclasses.filter(m => !m.description || m.description.length < 20)
  if (sessionsMissingDescription.length > 0) {
    attentionItems.push({
      id: 'desc_missing',
      icon: FileText,
      color: 'text-cinema-glow',
      bgColor: 'bg-cinema-glow/10 border-cinema-glow/30',
      title: `${sessionsMissingDescription.length} Topic${sessionsMissingDescription.length > 1 ? 's' : ''} Need Description`,
      description: `Add a short overview to "${sessionsMissingDescription[0].topic}" so attendees know what they will learn.`,
      actionText: 'Edit Topic',
      onClick: () => onNavigate('topics', 'topics'),
    })
  }

  return (
    <div className="space-y-8">
      {/* 4 Core Human KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
        {[
          { label: 'Total Registered', value: stats?.total || 0, sub: `${stats?.today || 0} registered today`, color: 'text-primary-600 dark:text-primary-400' },
          { label: 'Checked In', value: stats?.checked_in || 0, sub: `${stats?.total ? Math.round(((stats.checked_in || 0) / stats.total) * 100) : 0}% turn-out`, color: 'text-green-600 dark:text-green-400' },
          { label: 'Pending Review', value: pendingVerifications.length, sub: 'Guest payment proofs', color: pendingVerifications.length > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400' },
          { label: 'Topics & Programme', value: masterclasses.length, sub: `${speakers.length} speakers active`, color: 'text-purple-600 dark:text-purple-400' },
        ].map(({ label, value, sub, color }) => (
          <div key={label} className="stat-item">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">{label}</span>
            <p className={`text-3xl ${color} font-display font-semibold mt-1`}>{value}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{sub}</p>
          </div>
        ))}
      </div>

      {/* ⚡ Needs Attention Section (Triage Box) */}
      <div className="pt-6 border-t border-gray-200 dark:border-gray-800 space-y-4">
        <div className="section-header flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-primary-600 dark:bg-primary-400 animate-pulse" />
            <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">
              Action Required & Event Health
            </h3>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 500 }}>
            {attentionItems.length === 0 ? 'All systems optimal' : `${attentionItems.length} item${attentionItems.length > 1 ? 's' : ''} need review`}
          </span>
        </div>

        {attentionItems.length === 0 ? (
          <div className="flex items-center gap-3 p-4 border-l-4 border-green-500 bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300 text-xs" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <CheckCircle2 className="w-5 h-5 shrink-0 text-green-600 dark:text-green-400" />
            <div>
              <strong className="block text-green-700 dark:text-green-400">Everything is set up!</strong>
              <span>All sessions have speakers, photos are uploaded, and there are no unverified payments waiting.</span>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {attentionItems.map((item) => {
              const Icon = item.icon
              return (
                <div key={item.id} className={`p-3.5 border-l-4 flex items-start justify-between gap-3 ${item.bgColor}`}>
                  <div className="flex items-start gap-2.5 min-w-0">
                    <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${item.color}`} />
                    <div className="min-w-0">
                      <h4 className="text-xs font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{item.title}</h4>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={item.onClick}
                    className="cinema-btn-primary !px-2.5 !py-1 !min-h-0 text-[11px] shrink-0 whitespace-nowrap"
                    style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                  >
                    {item.actionText}
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Next Upcoming Session Spotlight */}
      {upcomingMC ? (
        <div className="pt-6 border-t border-gray-200 dark:border-gray-800 space-y-4">
          <div className="section-header flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <span className="text-xs text-primary-600 dark:text-primary-400 uppercase tracking-wider font-medium">
                Next Masterclass Session
              </span>
              <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white mt-0.5">{upcomingMC.topic}</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2.5 py-1 text-xs font-semibold border ${
                upcomingMC.registration_open
                  ? 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/30'
                  : 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/30'
              }`}>
                {upcomingMC.registration_open ? '● Registrations Open' : '● Registrations Closed'}
              </span>
              <button
                type="button"
                onClick={() => onNavigate('topics', 'topics')}
                className="cinema-btn-ghost !px-3 !py-1 !min-h-0 text-xs"
              >
                Manage Session
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            <div>
              <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wider block text-[10px]">Date & Time</span>
              <span className="text-gray-900 dark:text-white text-sm font-medium mt-0.5 block">
                {new Date(upcomingMC.event_date).toLocaleDateString('en-UG', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} at {upcomingMC.start_time || '5:00 PM'}
              </span>
            </div>
            <div>
              <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wider block text-[10px]">Featured Speaker</span>
              <span className="text-gray-900 dark:text-white text-sm font-medium mt-0.5 block">
                {upcomingMC.speaker_name || <span className="text-amber-600 dark:text-amber-400">No speaker assigned</span>}
              </span>
            </div>
            <div>
              <span className="text-gray-400 dark:text-gray-500 uppercase tracking-wider block text-[10px]">Current Attendance</span>
              <span className="text-gray-900 dark:text-white text-sm font-medium mt-0.5 block">
                <strong className="text-primary-600 dark:text-primary-400 font-bold">{upcomingMC.registration_count || 0}</strong> registered attendees
              </span>
            </div>
          </div>

          {upcomingMC.description && (
            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed pt-2 border-t border-gray-200 dark:border-gray-800" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {upcomingMC.description}
            </p>
          )}
        </div>
      ) : (
        <div className="pt-6 border-t border-gray-200 dark:border-gray-800 text-center space-y-3">
          <Calendar className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto" />
          <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">No Upcoming Masterclass Scheduled</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md mx-auto" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Schedule your next session or choose a curated topic from the Topic Library to start accepting attendee registrations.
          </p>
          <button
            type="button"
            onClick={() => onNavigate('topics', 'topics')}
            className="cinema-btn-primary !px-4 !py-2 text-xs font-semibold inline-flex items-center gap-1.5 mx-auto"
          >
            <Plus className="w-4 h-4" />
            Schedule Next Masterclass
          </button>
        </div>
      )}

      {/* Filter by Session Stats Breakdown */}
      <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
        <div className="section-header flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h4 className="text-lg font-display font-semibold text-gray-900 dark:text-white">Filter Roster by Session</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Select a masterclass session to view its isolated registration and check-in numbers.</p>
          </div>
          <div className="relative w-full sm:w-72">
            <select
              value={selectedMC}
              onChange={e => loadStats(e.target.value)}
              className="w-full appearance-none px-4 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 focus:outline-none focus:border-primary-500 pr-8"
              style={{ colorScheme: 'dark' }}
            >
              <option value="">All Masterclass Sessions</option>
              {masterclasses.map(m => (
                <option key={m.id} value={m.id}>
                  {new Date(m.event_date).toLocaleDateString()} – {m.topic.substring(0, 32)}…
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 dark:text-gray-500 pointer-events-none" />
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Topics & Programme In-Page Workspace ────────────────────────────────────
function TopicsProgrammeView({ onNavigateRegistrations }) {
  const [masterclasses, setMasterclasses] = useState([])
  const [speakers, setSpeakers] = useState([])
  const [loading, setLoading] = useState(true)

  // Workspace View State: 'upcoming' | 'library' | 'finished' | 'editor'
  const [topicWorkspaceMode, setTopicWorkspaceMode] = useState('upcoming')
  const [previousView, setPreviousView] = useState('upcoming')
  const [editingTopic, setEditingTopic] = useState(null)
  const [selectedFromLibrary, setSelectedFromLibrary] = useState(null)

  // Clone Session State
  const [cloningModal, setCloningModal] = useState(null)
  const [cloneDate, setCloneDate] = useState('')
  const [cloning, setCloning] = useState(null)
  const [copiedSlug, setCopiedSlug] = useState(null)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [downloadingFlyer, setDownloadingFlyer] = useState(null)
  const [regenerateModal, setRegenerateModal] = useState(null) // { mc, currentLayout, currentFont, altLayout, altFont, currentPreview, altPreview, loading, error }
  const [regenerating, setRegenerating] = useState(null)
  const [savingTemplate, setSavingTemplate] = useState(false)

  // Mark Finished & Reopen Modal Confirmation State
  const [finishConfirmModal, setFinishConfirmModal] = useState(null)
  const [finishing, setFinishing] = useState(false)
  const [reopenConfirmModal, setReopenConfirmModal] = useState(null)
  const [reopenOpenRegistration, setReopenOpenRegistration] = useState(false)
  const [reopening, setReopening] = useState(false)
  const [cancelConfirmModal, setCancelConfirmModal] = useState(null)
  const [cancelling, setCancelling] = useState(false)

  // Search & Filter State
  const [upcomingSearch, setUpcomingSearch] = useState('')
  const [librarySearch, setLibrarySearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All Topics')
  const [finishedSearch, setFinishedSearch] = useState('')
  const [previewTopic, setPreviewTopic] = useState(null)

  // In-Page Editor Form State
  const [form, setForm] = useState({
    id: null,
    topic: '',
    description: '',
    speaker_id: '',
    event_date: getNextLastFriday(),
    start_time: '17:00',
    duration_minutes: 120,
    category: 'Photography & Visual Storytelling',
    registration_open: true,
    is_active: true,
    venue: 'Synergy Multimedia Academy, Kampala',
    capacity: '',
    requirements: '',
    status: 'upcoming',
  })
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [mRes, sRes] = await Promise.all([
        masterclassService.adminGetMasterclasses(),
        masterclassService.getSpeakers(),
      ])
      setMasterclasses(mRes.masterclasses || [])
      setSpeakers(sRes.speakers || [])
    } catch (err) {
      console.error(err)
      toast.error('Failed to load programme')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Escape key closes preview or modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (previewTopic) setPreviewTopic(null)
        if (finishConfirmModal) setFinishConfirmModal(null)
        if (reopenConfirmModal) setReopenConfirmModal(null)
        if (cancelConfirmModal) setCancelConfirmModal(null)
        if (regenerateModal) handleCloseRegenerate()
        if (cloningModal) setCloningModal(null)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [previewTopic, finishConfirmModal, reopenConfirmModal, cancelConfirmModal, cloningModal])

  // PDF Catalogue Download
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true)
    try {
      toast.loading('Generating Topics Catalogue PDF...', { id: 'topics-pdf' })
      await masterclassService.adminDownloadTopicsPdf()
      toast.success('Topics Catalogue PDF downloaded!', { id: 'topics-pdf' })
    } catch (err) {
      console.error(err)
      toast.error(err.message || 'Failed to download PDF catalogue', { id: 'topics-pdf' })
    } finally {
      setDownloadingPdf(false)
    }
  }

  // Copy Public Registration Link
  const handleCopyLink = (slugOrId) => {
    const url = `${window.location.origin}/masterclass/${slugOrId}`
    navigator.clipboard.writeText(url).then(() => {
      setCopiedSlug(slugOrId)
      toast.success('Public registration link copied!')
      setTimeout(() => setCopiedSlug(null), 2000)
    })
  }

  // Download 1080x1080 flyer PNG for a session
  const handleDownloadFlyer = async (mc) => {
    const id = mc.slug || mc.id
    setDownloadingFlyer(id)
    try {
      await masterclassService.adminDownloadFlyer(mc.id)
      toast.success('Flyer downloaded')
    } catch (err) {
      toast.error(err.message || 'Failed to download flyer')
    } finally {
      setDownloadingFlyer(null)
    }
  }

  // ── Regenerate Flyer: cycle through templates + font pairings ──────────────
  // Constants imported from shared flyerConstants module (single source of truth)
  const { LAYOUT_NAMES, FONT_PAIRINGS, cycleLayout, cycleFontPairing } = flyerConstants

  // Open the regenerate modal with current vs alternative previews
  const handleRegenerateFlyer = async (mc) => {
    const currentLayout = mc.flyer_layout || 'B'
    const currentFont = mc.font_pairing || null
    const altLayout = cycleLayout(currentLayout)
    const altFont = cycleFontPairing(currentFont, altLayout)
    setRegenerateModal({ mc, currentLayout, currentFont, altLayout, altFont, currentPreview: null, altPreview: null, loading: true, error: null })
    setRegenerating(mc.slug || mc.id)
    try {
      // Fetch both previews in parallel — current uses persisted font, alt uses cycled font
      const [currentRes, altRes] = await Promise.all([
        masterclassService.adminPreviewFlyerLayout(mc.id, currentLayout, currentFont),
        masterclassService.adminPreviewFlyerLayout(mc.id, altLayout, altFont),
      ])
      setRegenerateModal(prev => ({
        ...prev,
        currentPreview: currentRes.blobUrl,
        altPreview: altRes.blobUrl,
        loading: false,
      }))
    } catch (err) {
      setRegenerateModal(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Unable to render this design. Your current flyer has been preserved.',
      }))
    } finally {
      setRegenerating(null)
    }
  }

  // Try another template + font (cycle to the next combination)
  const handleTryAnother = async () => {
    if (!regenerateModal || regenerateModal.loading) return
    const nextAlt = cycleLayout(regenerateModal.altLayout)
    const nextFont = cycleFontPairing(regenerateModal.altFont, nextAlt)
    // Revoke old alt preview
    if (regenerateModal.altPreview) window.URL.revokeObjectURL(regenerateModal.altPreview)
    setRegenerateModal(prev => ({ ...prev, altLayout: nextAlt, altFont: nextFont, altPreview: null, loading: true, error: null }))
    try {
      const res = await masterclassService.adminPreviewFlyerLayout(regenerateModal.mc.id, nextAlt, nextFont)
      setRegenerateModal(prev => ({ ...prev, altPreview: res.blobUrl, loading: false }))
    } catch (err) {
      setRegenerateModal(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Unable to render this design. Your current flyer has been preserved.',
      }))
    }
  }

  // Save the alternative as the selected template + font
  const handleUseThisDesign = async () => {
    if (!regenerateModal || !regenerateModal.altLayout) return
    setSavingTemplate(true)
    try {
      await masterclassService.adminSaveFlyerTemplate(regenerateModal.mc.id, regenerateModal.altLayout, regenerateModal.altFont)
      const fontName = regenerateModal.altFont ? FONT_PAIRINGS[regenerateModal.altFont] : ''
      toast.success(`${LAYOUT_NAMES[regenerateModal.altLayout]}${fontName ? ` · ${fontName}` : ''} saved as the flyer design`)
      // Update the mc in local state so the button reflects the new template
      load()
      handleCloseRegenerate()
    } catch (err) {
      toast.error(err.message || 'Failed to save flyer template')
    } finally {
      setSavingTemplate(false)
    }
  }

  // Keep current design — just close the modal
  const handleKeepCurrent = () => {
    handleCloseRegenerate()
    toast.success('Current design kept')
  }

  const handleCloseRegenerate = () => {
    if (regenerateModal?.currentPreview) window.URL.revokeObjectURL(regenerateModal.currentPreview)
    if (regenerateModal?.altPreview) window.URL.revokeObjectURL(regenerateModal.altPreview)
    setRegenerateModal(null)
  }

  // Clone Session Handler
  const handleClone = async () => {
    if (!cloningModal || !cloneDate) return
    setCloning(cloningModal.id)
    try {
      await masterclassService.adminCloneMasterclass(cloningModal.id, { event_date: cloneDate })
      toast.success('Session cloned for new date!')
      setCloningModal(null)
      load()
    } catch (err) {
      toast.error(err.message || 'Failed to clone session')
    } finally {
      setCloning(null)
    }
  }

  // Toggle Registration Open/Closed
  const handleToggleOpen = async (mc) => {
    try {
      await masterclassService.adminUpdateMasterclass(mc.id, {
        ...mc,
        registration_open: !mc.registration_open,
      })
      toast.success(mc.registration_open ? 'Registrations closed' : 'Registrations opened!')
      load()
    } catch (err) {
      toast.error(err.message || 'Failed to update registration status')
    }
  }

  // Mark Session as Finished (Confirmed)
  const handleConfirmMarkFinished = async () => {
    if (!finishConfirmModal) return
    setFinishing(true)
    try {
      await masterclassService.adminUpdateMasterclass(finishConfirmModal.id, {
        ...finishConfirmModal,
        status: 'completed',
        registration_open: false,
      })
      toast.success(`"${finishConfirmModal.topic}" marked as finished and moved to historical archive.`)
      setFinishConfirmModal(null)
      load()
    } catch (err) {
      toast.error(err.message || 'Failed to mark session as finished')
    } finally {
      setFinishing(false)
    }
  }

  // Reopen Finished Session (Confirmed)
  const handleConfirmReopenSession = async () => {
    if (!reopenConfirmModal) return
    setReopening(true)
    try {
      await masterclassService.adminUpdateMasterclass(reopenConfirmModal.id, {
        ...reopenConfirmModal,
        status: 'upcoming',
        registration_open: reopenOpenRegistration,
      })
      toast.success(
        reopenOpenRegistration
          ? `"${reopenConfirmModal.topic}" reopened with registrations OPEN.`
          : `"${reopenConfirmModal.topic}" reopened with registrations CLOSED (safe mode).`
      )
      setReopenConfirmModal(null)
      load()
    } catch (err) {
      toast.error(err.message || 'Failed to reopen session')
    } finally {
      setReopening(false)
    }
  }

  // Cancel Session (Confirmed)
  const handleConfirmCancelSession = async () => {
    if (!cancelConfirmModal) return
    setCancelling(true)
    try {
      await masterclassService.adminUpdateMasterclass(cancelConfirmModal.id, {
        ...cancelConfirmModal,
        status: 'cancelled',
        registration_open: false,
      })
      toast.success(`"${cancelConfirmModal.topic}" has been cancelled.`)
      setCancelConfirmModal(null)
      load()
    } catch (err) {
      toast.error(err.message || 'Failed to cancel session')
    } finally {
      setCancelling(false)
    }
  }

  // Open In-Page Editor for New Custom Topic
  const handleStartCreateCustom = () => {
    setForm({
      id: null,
      topic: '',
      description: '',
      speaker_id: '',
      event_date: getNextLastFriday(),
      start_time: '17:00',
      duration_minutes: 120,
      category: 'Photography & Visual Storytelling',
      registration_open: true,
      is_active: true,
      venue: 'Synergy Multimedia Academy, Kampala',
      capacity: '',
      requirements: '',
      status: 'upcoming',
    })
    setEditingTopic(null)
    setSelectedFromLibrary(null)
    setPreviousView(topicWorkspaceMode)
    setTopicWorkspaceMode('editor')
  }

  // Open In-Page Editor for Editing Existing Topic
  const handleStartEditTopic = (mc) => {
    setForm({
      id: mc.id,
      topic: mc.topic || '',
      description: mc.description || '',
      speaker_id: mc.speaker_id || '',
      event_date: mc.event_date ? mc.event_date.substring(0, 10) : new Date().toISOString().substring(0, 10),
      start_time: mc.start_time || '17:00',
      duration_minutes: mc.duration_minutes || 120,
      category: mc.category || 'Photography & Visual Storytelling',
      registration_open: mc.registration_open ?? true,
      is_active: mc.is_active ?? true,
      venue: mc.venue || 'Synergy Multimedia Academy, Kampala',
      capacity: mc.capacity || '',
      requirements: mc.requirements || '',
      status: mc.status || 'upcoming',
    })
    setEditingTopic(mc)
    setSelectedFromLibrary(null)
    setPreviousView(topicWorkspaceMode)
    setTopicWorkspaceMode('editor')
  }

  // "Add to Programme" from Library Selection
  const handleSelectLibraryTopic = (item) => {
    setForm({
      id: null,
      topic: item.title,
      description: item.description,
      speaker_id: '',
      event_date: getNextLastFriday(),
      start_time: '17:00',
      duration_minutes: item.duration_minutes || 120,
      category: item.category,
      registration_open: true,
      is_active: true,
      venue: 'Synergy Multimedia Academy, Kampala',
      capacity: '',
      requirements: '',
      status: 'upcoming',
    })
    setEditingTopic(null)
    setSelectedFromLibrary(item.title)
    setPreviousView('library')
    setTopicWorkspaceMode('editor')
    toast.success(`Selected "${item.title}". Customise details as needed.`)
  }

  // Save In-Page Editor Form Submission
  const handleSaveForm = async (e) => {
    e.preventDefault()
    if (!form.topic.trim() || !form.event_date) {
      toast.error('Topic title and event date are required.')
      return
    }
    setSaving(true)
    try {
      if (form.id) {
        await masterclassService.adminUpdateMasterclass(form.id, form)
        toast.success('Topic updated successfully!')
      } else {
        await masterclassService.adminCreateMasterclass(form)
        toast.success('Topic scheduled to programme!')
      }
      setTopicWorkspaceMode(form.status === 'completed' ? 'finished' : 'upcoming')
      setEditingTopic(null)
      setSelectedFromLibrary(null)
      load()
    } catch (err) {
      toast.error(err.message || 'Failed to save topic')
    } finally {
      setSaving(false)
    }
  }

  // ── Separation: Upcoming vs Finished Sessions ──────────────────────────────
  const upcomingMasterclasses = useMemo(() => {
    return masterclasses.filter(mc => {
      const status = (mc.status || 'upcoming').toLowerCase()
      const isFinished = status === 'completed'
      const isCancelled = status === 'cancelled'
      const isDraft = status === 'draft'
      const isArchived = mc.is_archived === true
      const isInactive = mc.is_active === false

      // Active programme: must not be finished, cancelled, draft, archived, or inactive
      return !isFinished && !isCancelled && !isDraft && !isArchived && !isInactive
    })
  }, [masterclasses])

  const finishedMasterclasses = useMemo(() => {
    return masterclasses.filter(mc => {
      const status = (mc.status || '').toLowerCase()
      return status === 'completed' || mc.is_archived === true
    })
  }, [masterclasses])

  // Filtered Upcoming List
  const filteredUpcoming = useMemo(() => {
    if (!upcomingSearch.trim()) return upcomingMasterclasses
    const term = upcomingSearch.toLowerCase()
    return upcomingMasterclasses.filter((mc) => {
      const spk = speakers.find(s => s.id === mc.speaker_id)
      return (
        (mc.topic && mc.topic.toLowerCase().includes(term)) ||
        (mc.category && mc.category.toLowerCase().includes(term)) ||
        (spk && spk.name.toLowerCase().includes(term))
      )
    })
  }, [upcomingMasterclasses, upcomingSearch, speakers])

  // Filtered Finished List
  const filteredFinished = useMemo(() => {
    if (!finishedSearch.trim()) return finishedMasterclasses
    const term = finishedSearch.toLowerCase()
    return finishedMasterclasses.filter((mc) => {
      const spk = speakers.find(s => s.id === mc.speaker_id)
      return (
        (mc.topic && mc.topic.toLowerCase().includes(term)) ||
        (mc.category && mc.category.toLowerCase().includes(term)) ||
        (spk && spk.name.toLowerCase().includes(term))
      )
    })
  }, [finishedMasterclasses, finishedSearch, speakers])

  // Category counts for Library Sidebar
  const categoryCounts = useMemo(() => {
    const counts = { 'All Topics': MASTERCLASS_TOPIC_LIBRARY.length }
    for (const t of MASTERCLASS_TOPIC_LIBRARY) {
      counts[t.category] = (counts[t.category] || 0) + 1
    }
    return counts
  }, [])

  const categoriesList = useMemo(() => {
    return ['All Topics', ...MASTERCLASS_TOPIC_CATEGORIES.filter(c => c !== 'All Categories')]
  }, [])

  // Filtered Topic Library List
  const filteredLibrary = useMemo(() => {
    const term = librarySearch.trim().toLowerCase()
    return MASTERCLASS_TOPIC_LIBRARY.filter((item) => {
      const matchesCategory = selectedCategory === 'All Topics' || item.category === selectedCategory
      if (!term) return matchesCategory

      const inTitle = item.title.toLowerCase().includes(term)
      const inDesc = item.description.toLowerCase().includes(term)
      const inCat = item.category.toLowerCase().includes(term)
      const inTakeaways = item.suggested_takeaways?.some(tk =>
        tk.label.toLowerCase().includes(term) || tk.description.toLowerCase().includes(term)
      )
      return matchesCategory && (inTitle || inDesc || inCat || inTakeaways)
    })
  }, [librarySearch, selectedCategory])

  const libraryResultsText = useMemo(() => {
    const count = filteredLibrary.length
    if (librarySearch.trim()) {
      return `${count} ${count === 1 ? 'topic' : 'topics'} found for "${librarySearch.trim()}"`
    }
    if (selectedCategory !== 'All Topics') {
      return `${count} ${count === 1 ? 'topic' : 'topics'} in this category`
    }
    return `${count} topics available`
  }, [filteredLibrary.length, librarySearch, selectedCategory])

  return (
    <div className="space-y-6">
      {/* ── Main Workspace Header ────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-white text-lg sm:text-xl font-bold">Topics & Programme</h2>
            {topicWorkspaceMode === 'editor' && (
              <span className="text-[11px] font-mono uppercase px-2 py-0.5 bg-cinema-glow/20 text-cinema-glow border border-cinema-glow/30">
                {form.id ? 'Editing Session' : 'New Session'}
              </span>
            )}
          </div>
          <p className="text-xs text-cinema-muted font-sans mt-0.5">
            Manage upcoming scheduled sessions, permanent topic catalogue, and past archives.
          </p>
        </div>

        {/* Global Action Buttons */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            className="cinema-btn-ghost !px-3 !py-2 !min-h-0 text-xs inline-flex items-center gap-1.5 border border-white/10 hover:border-cinema-glow/40"
            title="Download formatted speaker-facing topics catalogue PDF"
          >
            {downloadingPdf ? <LoadingSpinner size="sm" inline /> : <FileDown className="w-4 h-4 text-cinema-gold" />}
            Download Topics PDF
          </button>

          {topicWorkspaceMode !== 'editor' ? (
            <button
              type="button"
              onClick={handleStartCreateCustom}
              className="cinema-btn-primary !px-4 !py-2 !min-h-0 text-xs sm:text-sm inline-flex items-center gap-1.5 shrink-0"
            >
              <Plus className="w-4 h-4" />
              Add Topic
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setTopicWorkspaceMode(previousView || 'upcoming')}
              className="cinema-btn-ghost !px-3 !py-2 !min-h-0 text-xs inline-flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Programme
            </button>
          )}
        </div>
      </div>

      {/* ── Top-Level Workspace Sub-Navigation (Upcoming | Topic Library | Finished) ── */}
      {topicWorkspaceMode !== 'editor' && (
        <div className="flex items-center gap-2 border-b border-white/10 pb-3 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => setTopicWorkspaceMode('upcoming')}
            className={`px-4 py-2 text-xs font-sans font-semibold border transition-all inline-flex items-center gap-2 ${
              topicWorkspaceMode === 'upcoming'
                ? 'bg-cinema-glow/20 text-cinema-glow border-cinema-glow shadow-[0_0_12px_rgba(238,141,33,0.15)] font-bold'
                : 'bg-cinema-graphite/40 text-cinema-muted hover:text-white border-white/10'
            }`}
          >
            <Calendar className="w-3.5 h-3.5" />
            Upcoming ({upcomingMasterclasses.length})
          </button>

          <button
            type="button"
            onClick={() => setTopicWorkspaceMode('library')}
            className={`px-4 py-2 text-xs font-sans font-semibold border transition-all inline-flex items-center gap-2 ${
              topicWorkspaceMode === 'library'
                ? 'bg-cinema-glow/20 text-cinema-glow border-cinema-glow shadow-[0_0_12px_rgba(238,141,33,0.15)] font-bold'
                : 'bg-cinema-graphite/40 text-cinema-muted hover:text-white border-white/10'
            }`}
          >
            <BookOpen className="w-3.5 h-3.5" />
            Topic Library (72)
          </button>

          <button
            type="button"
            onClick={() => setTopicWorkspaceMode('finished')}
            className={`px-4 py-2 text-xs font-sans font-semibold border transition-all inline-flex items-center gap-2 ${
              topicWorkspaceMode === 'finished'
                ? 'bg-cinema-glow/20 text-cinema-glow border-cinema-glow shadow-[0_0_12px_rgba(238,141,33,0.15)] font-bold'
                : 'bg-cinema-graphite/40 text-cinema-muted hover:text-white border-white/10'
            }`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Finished ({finishedMasterclasses.length})
          </button>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* VIEW 1: UPCOMING SESSIONS                                               */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {topicWorkspaceMode === 'upcoming' && (
        <div className="space-y-4">
          {/* Auto-Scheduler Notice & Search */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 border border-cinema-glow/20 bg-cinema-glow/5 px-4 py-3 text-cinema-soft/80 font-sans text-xs flex items-center justify-between gap-4">
              <div>
                <strong className="text-cinema-glow">Auto-Schedule Active:</strong>{' '}
                Monthly sessions held on the {SCHEDULE_LABEL.toLowerCase()} (5:00 PM).
              </div>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cinema-muted" />
              <input
                type="text"
                placeholder="Search upcoming topics..."
                value={upcomingSearch}
                onChange={e => setUpcomingSearch(e.target.value)}
                className="w-full bg-cinema-graphite border border-white/10 pl-9 pr-3 py-2 text-xs text-white placeholder-cinema-muted focus:outline-none focus:border-cinema-glow"
              />
            </div>
          </div>

          {/* Masterclasses List */}
          {loading ? (
            <LoadingSpinner className="py-16" />
          ) : filteredUpcoming.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title={upcomingSearch ? "No matching upcoming topics found" : "No upcoming Masterclasses"}
              description={upcomingSearch ? "Try adjusting your search query." : "Schedule your next session or choose a topic from the Topic Library to start accepting registrations."}
              actionText={upcomingSearch ? "Clear Search" : "Schedule Next Masterclass"}
              onAction={() => {
                if (upcomingSearch) setUpcomingSearch('')
                else handleStartCreateCustom()
              }}
            />
          ) : (
            <div className="space-y-3">
              {filteredUpcoming.map((mc, idx) => {
                const speaker = speakers.find(s => s.id === mc.speaker_id)
                return (
                  <div
                    key={mc.id}
                    className="p-4 border border-white/10 bg-cinema-graphite/30 hover:bg-cinema-graphite/50 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div className="flex items-start gap-3.5 min-w-0">
                      <div className="w-8 h-8 flex items-center justify-center bg-white/5 border border-white/10 text-xs font-mono font-bold text-cinema-gold shrink-0">
                        {idx + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-sans text-white font-semibold text-sm truncate">{mc.topic}</h3>
                          {mc.category && (
                            <span className="text-[10px] px-2 py-0.5 bg-white/5 border border-white/10 text-cinema-muted font-sans">
                              {mc.category}
                            </span>
                          )}
                          {mc.flyer_layout && (
                            <span className="text-[10px] px-2 py-0.5 bg-orange-400/10 border border-orange-400/20 text-orange-300 font-sans" title="Current flyer design">
                              Design: {LAYOUT_NAMES[mc.flyer_layout] || mc.flyer_layout}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => handleToggleOpen(mc)}
                            className={`text-[10px] uppercase font-bold px-2 py-0.5 border cursor-pointer transition-colors ${
                              mc.registration_open
                                ? 'bg-green-500/10 text-green-400 border-green-500/30 hover:bg-green-500/20'
                                : 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20'
                            }`}
                            title="Click to toggle registration status"
                          >
                            {mc.registration_open ? '● Open' : '● Closed'}
                          </button>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 mt-1.5 text-xs text-cinema-muted font-sans">
                          <span className="flex items-center gap-1 text-gray-300">
                            <Calendar className="w-3.5 h-3.5 text-cinema-gold" />
                            {new Date(mc.event_date).toLocaleDateString('en-UG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} at {mc.start_time || '5:00 PM'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mic className="w-3.5 h-3.5 text-cinema-glow" />
                            {speaker ? (
                              <span className="text-white font-medium">{speaker.name}</span>
                            ) : (
                              <span className="text-amber-400 italic">No speaker assigned</span>
                            )}
                          </span>
                          <span>
                            <strong className="text-cinema-glow">{mc.registration_count || 0}</strong> registered
                          </span>
                          <span>{mc.duration_minutes || 120} mins</span>
                        </div>

                        {mc.description && (
                          <p className="text-xs text-cinema-muted/70 mt-1 line-clamp-1">
                            {mc.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center flex-wrap justify-end">
                      <button
                        type="button"
                        onClick={() => handleCopyLink(mc.slug || mc.id)}
                        className="cinema-btn-ghost !p-2 !min-h-0"
                        title="Copy public registration link"
                      >
                        {copiedSlug === (mc.slug || mc.id) ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <LinkIcon className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setCloningModal(mc); setCloneDate(getSuggestedCloneDate(mc.event_date)) }}
                        className="cinema-btn-ghost !p-2 !min-h-0"
                        title="Clone to new date"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRegenerateFlyer(mc)}
                        disabled={regenerating === (mc.slug || mc.id)}
                        className="cinema-btn-ghost !p-2 !min-h-0 disabled:opacity-50"
                        title="Regenerate flyer — try another design"
                      >
                        {regenerating === (mc.slug || mc.id) ? (
                          <LoadingSpinner size="sm" inline />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadFlyer(mc)}
                        disabled={downloadingFlyer === (mc.slug || mc.id)}
                        className="cinema-btn-ghost !p-2 !min-h-0 disabled:opacity-50"
                        title="Download 1080x1080 flyer PNG"
                      >
                        {downloadingFlyer === (mc.slug || mc.id) ? (
                          <LoadingSpinner size="sm" inline />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStartEditTopic(mc)}
                        className="cinema-btn-ghost !px-3 !py-2 !min-h-0 text-xs inline-flex items-center gap-1"
                        title="Edit session details"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setFinishConfirmModal(mc)}
                        className="cinema-btn-ghost !px-2.5 !py-2 !min-h-0 text-xs text-green-400 hover:bg-green-500/10 border border-green-500/30 inline-flex items-center gap-1"
                        title="Mark session as completed and move to historical archive"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Mark Finished
                      </button>
                      <button
                        type="button"
                        onClick={() => setCancelConfirmModal(mc)}
                        className="cinema-btn-ghost !px-2.5 !py-2 !min-h-0 text-xs text-red-400 hover:bg-red-500/10 border border-red-500/30 inline-flex items-center gap-1"
                        title="Cancel this masterclass session"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Cancel
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* VIEW 2: IN-PAGE TOPIC LIBRARY CATALOGUE                                  */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {topicWorkspaceMode === 'library' && (
        <div className="space-y-4">
          {/* Subheader & Quick Action */}
          <div className="section-header flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">Curated Topic Library</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Browse 72 pre-designed creative curriculum topics ready to add to your masterclass schedule.
              </p>
            </div>
            <button
              type="button"
              onClick={handleStartCreateCustom}
              className="cinema-btn-ghost !px-3 !py-1.5 text-xs text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-500/30 inline-flex items-center gap-1.5 self-start sm:self-center"
            >
              <Edit className="w-3.5 h-3.5" />
              Create Custom Topic Instead
            </button>
          </div>

          {/* Two-Column In-Page Catalogue Layout */}
          <div className="flex flex-col md:flex-row gap-5 items-start">
            {/* Left Column: Categories Sidebar */}
            <div className="w-full md:w-60 shrink-0 border border-white/10 bg-cinema-charcoal p-3">
              <div className="text-[11px] font-mono uppercase tracking-wider text-cinema-muted/70 font-semibold mb-2 px-1">
                Categories
              </div>

              {/* Desktop Vertical List */}
              <div className="hidden md:flex flex-col gap-1">
                {categoriesList.map((cat) => {
                  const isActive = selectedCategory === cat
                  const count = categoryCounts[cat] || 0
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setSelectedCategory(cat)}
                      className={`w-full text-left px-2.5 py-2 text-xs font-sans transition-colors flex items-center justify-between border ${
                        isActive
                          ? 'bg-cinema-glow/20 text-cinema-glow border-cinema-glow/40 font-semibold'
                          : 'bg-transparent text-cinema-muted hover:text-white hover:bg-white/[0.03] border-transparent'
                      }`}
                    >
                      <span className="truncate pr-2">{cat}</span>
                      <span className={`text-[10px] px-1.5 py-0.2 font-mono shrink-0 ${
                        isActive ? 'bg-cinema-glow/30 text-white' : 'bg-white/5 text-cinema-muted'
                      }`}>
                        {count}
                      </span>
                    </button>
                  )
                })}
              </div>

              {/* Mobile Horizontal Pills */}
              <div className="flex md:hidden gap-1.5 overflow-x-auto no-scrollbar pb-1">
                {categoriesList.map((cat) => {
                  const isActive = selectedCategory === cat
                  const count = categoryCounts[cat] || 0
                  return (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setSelectedCategory(cat)}
                      className={`px-2.5 py-1 text-[11px] font-sans whitespace-nowrap border transition-colors shrink-0 flex items-center gap-1.5 ${
                        isActive
                          ? 'bg-cinema-glow/20 text-cinema-glow border-cinema-glow font-semibold'
                          : 'bg-cinema-graphite/40 text-cinema-muted hover:text-white border-white/5'
                      }`}
                    >
                      <span>{cat}</span>
                      <span className="text-[10px] opacity-70 font-mono">({count})</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Right Column: Search + Topic List */}
            <div className="flex-1 w-full space-y-3">
              {/* Search & Dynamic Counter */}
              <div className="space-y-1.5">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted" />
                  <input
                    type="text"
                    placeholder="Search topics by keyword (e.g. lighting, pricing, branding, AI)..."
                    value={librarySearch}
                    onChange={e => setLibrarySearch(e.target.value)}
                    className="w-full bg-cinema-graphite border border-white/10 pl-9 pr-3 py-2 text-xs text-white placeholder-cinema-muted focus:outline-none focus:border-cinema-glow"
                  />
                  {librarySearch && (
                    <button
                      type="button"
                      onClick={() => setLibrarySearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-cinema-muted hover:text-white text-xs"
                    >
                      ✕
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between text-[11px] text-cinema-muted font-sans px-1">
                  <span>{libraryResultsText}</span>
                  {(librarySearch || selectedCategory !== 'All Topics') && (
                    <button
                      type="button"
                      onClick={() => { setLibrarySearch(''); setSelectedCategory('All Topics') }}
                      className="text-cinema-glow hover:underline"
                    >
                      Reset filters
                    </button>
                  )}
                </div>
              </div>

              {/* Topics Compact Rows */}
              {filteredLibrary.length === 0 ? (
                <div className="p-8 text-center border border-dashed border-white/10 bg-cinema-graphite/20">
                  <Search className="w-6 h-6 text-cinema-muted mx-auto mb-2 opacity-50" />
                  <h4 className="text-white text-xs font-semibold font-sans">No topics found</h4>
                  <p className="text-xs text-cinema-muted font-sans mt-1">
                    Try a different search or select "All Topics".
                  </p>
                  <button
                    type="button"
                    onClick={() => { setLibrarySearch(''); setSelectedCategory('All Topics') }}
                    className="cinema-btn-ghost !px-3 !py-1 text-xs mt-3 border border-white/10"
                  >
                    Show All Topics
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredLibrary.map(item => (
                    <div
                      key={item.id}
                      className="p-3.5 border border-white/10 bg-cinema-graphite/30 hover:bg-cinema-graphite/60 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-white text-xs sm:text-sm font-semibold font-sans">
                            {item.title}
                          </h4>
                          <span className="text-[10px] px-1.5 py-0.5 bg-white/5 border border-white/10 text-cinema-gold font-sans shrink-0">
                            {item.category}
                          </span>
                          <span className="text-[10px] text-cinema-muted font-mono shrink-0">
                            {item.duration_minutes || 120} min
                          </span>
                        </div>
                        <p className="text-xs text-cinema-soft/80 font-sans line-clamp-2">
                          {item.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                        <button
                          type="button"
                          onClick={() => setPreviewTopic(item)}
                          className="cinema-btn-ghost !px-2.5 !py-1.5 !min-h-0 text-xs text-cinema-muted hover:text-white border border-white/10"
                          title="Preview full description and key takeaways"
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSelectLibraryTopic(item)}
                          className="cinema-btn-primary !px-3.5 !py-1.5 !min-h-0 text-xs font-semibold inline-flex items-center gap-1"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add to Programme
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* VIEW 3: FINISHED MASTERCLASSES                                           */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {topicWorkspaceMode === 'finished' && (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 border border-white/10 bg-cinema-graphite/30 px-4 py-3 text-cinema-soft/80 font-sans text-xs flex items-center justify-between">
              <div>
                <strong className="text-white font-semibold">Historical Archive:</strong>{' '}
                Finished masterclasses are preserved with full attendance, speaker, and registration records.
              </div>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cinema-muted" />
              <input
                type="text"
                placeholder="Search finished sessions..."
                value={finishedSearch}
                onChange={e => setFinishedSearch(e.target.value)}
                className="w-full bg-cinema-graphite border border-white/10 pl-9 pr-3 py-2 text-xs text-white placeholder-cinema-muted focus:outline-none focus:border-cinema-glow"
              />
            </div>
          </div>

          {filteredFinished.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title={finishedSearch ? "No matching finished topics" : "No finished Masterclasses yet"}
              description={finishedSearch ? "Try adjusting your search query." : "Completed sessions will appear here after you mark them as finished."}
              actionText={finishedSearch ? "Clear Search" : "View Topic Library"}
              onAction={() => {
                if (finishedSearch) setFinishedSearch('')
                else setTopicWorkspaceMode('library')
              }}
            />
          ) : (
            <div className="space-y-3">
              {filteredFinished.map((mc, idx) => {
                const speaker = speakers.find(s => s.id === mc.speaker_id)
                return (
                  <div
                    key={mc.id}
                    className="p-4 border border-white/10 bg-cinema-graphite/20 hover:bg-cinema-graphite/40 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4 opacity-90"
                  >
                    <div className="flex items-start gap-3.5 min-w-0">
                      <div className="w-8 h-8 flex items-center justify-center bg-green-500/10 border border-green-500/20 text-xs font-mono font-bold text-green-400 shrink-0">
                        ✓
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-sans text-white font-semibold text-sm truncate">{mc.topic}</h3>
                          {mc.category && (
                            <span className="text-[10px] px-2 py-0.5 bg-white/5 border border-white/10 text-cinema-muted font-sans">
                              {mc.category}
                            </span>
                          )}
                          {mc.flyer_layout && (
                            <span className="text-[10px] px-2 py-0.5 bg-orange-400/10 border border-orange-400/20 text-orange-300 font-sans" title="Current flyer design">
                              Design: {LAYOUT_NAMES[mc.flyer_layout] || mc.flyer_layout}
                            </span>
                          )}
                          <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-green-500/10 text-green-400 border border-green-500/30">
                            Finished
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-4 mt-1.5 text-xs text-cinema-muted font-sans">
                          <span className="flex items-center gap-1 text-gray-300">
                            <Calendar className="w-3.5 h-3.5 text-cinema-gold" />
                            {new Date(mc.event_date).toLocaleDateString('en-UG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mic className="w-3.5 h-3.5 text-cinema-glow" />
                            {speaker ? speaker.name : 'No speaker'}
                          </span>
                          <span>
                            <strong className="text-white">{mc.registration_count || 0}</strong> registrations • <strong className="text-green-400">{mc.checked_in_count || mc.attendees_count || 0}</strong> attended
                          </span>
                          <span>{mc.duration_minutes || 120} mins</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                      {onNavigateRegistrations && (
                        <button
                          type="button"
                          onClick={() => onNavigateRegistrations(mc.id)}
                          className="cinema-btn-ghost !px-3 !py-2 !min-h-0 text-xs inline-flex items-center gap-1 text-cinema-muted hover:text-white"
                          title="View registration records for this finished session"
                        >
                          <Users className="w-3.5 h-3.5" />
                          View Registrations
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRegenerateFlyer(mc)}
                        disabled={regenerating === (mc.slug || mc.id)}
                        className="cinema-btn-ghost !p-2 !min-h-0 disabled:opacity-50"
                        title="Regenerate flyer — try another design"
                      >
                        {regenerating === (mc.slug || mc.id) ? (
                          <LoadingSpinner size="sm" inline />
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDownloadFlyer(mc)}
                        disabled={downloadingFlyer === (mc.slug || mc.id)}
                        className="cinema-btn-ghost !p-2 !min-h-0 disabled:opacity-50"
                        title="Download 1080x1080 flyer PNG"
                      >
                        {downloadingFlyer === (mc.slug || mc.id) ? (
                          <LoadingSpinner size="sm" inline />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStartEditTopic(mc)}
                        className="cinema-btn-ghost !px-3 !py-2 !min-h-0 text-xs inline-flex items-center gap-1"
                        title="Edit session records"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => setReopenConfirmModal(mc)}
                        className="cinema-btn-ghost !px-3 !py-2 !min-h-0 text-xs text-cinema-glow border border-cinema-glow/30 inline-flex items-center gap-1"
                        title="Reopen and return to upcoming schedule"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Reopen
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ──────────────────────────────────────────────────────────────────────── */}
      {/* VIEW 4: IN-PAGE TOPIC CREATION & EDIT WORKSPACE (NO GIANT MODAL)        */}
      {/* ──────────────────────────────────────────────────────────────────────── */}
      {topicWorkspaceMode === 'editor' && (
        <div className="pt-6 border-t border-gray-200 dark:border-gray-800 space-y-6">
          {/* Back Banner */}
          <div className="section-header flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <button
                type="button"
                onClick={() => setTopicWorkspaceMode(previousView || 'upcoming')}
                className="text-xs text-cinema-muted hover:text-white font-sans inline-flex items-center gap-1.5 transition-colors mb-1.5"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to {previousView === 'library' ? 'Topic Library' : previousView === 'finished' ? 'Finished Sessions' : 'Upcoming Programme'}
              </button>
              <h3 className="font-display text-white text-lg sm:text-xl font-bold">
                {form.id ? 'Edit Masterclass Topic' : selectedFromLibrary ? 'Schedule Selected Library Topic' : 'Add Masterclass Topic'}
              </h3>
              <p className="text-xs text-cinema-muted font-sans mt-0.5">
                {form.id
                  ? 'Update session topic, date, speaker assignment, and logistics.'
                  : 'Configure details below to schedule this masterclass onto the programme.'}
              </p>
            </div>

            {!form.id && (
              <button
                type="button"
                onClick={() => setTopicWorkspaceMode('library')}
                className="cinema-btn-ghost !px-3 !py-1.5 text-xs text-cinema-glow border border-cinema-glow/30 inline-flex items-center gap-1.5 self-start sm:self-center"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Browse Topic Library (72)
              </button>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSaveForm} className="space-y-5">
            {/* Library Pre-fill Banner */}
            {selectedFromLibrary && (
              <div className="flex items-center justify-between p-3 bg-cinema-glow/10 border border-cinema-glow/30 text-xs font-sans text-cinema-glow">
                <span className="flex items-center gap-2 min-w-0">
                  <Sparkles className="w-4 h-4 shrink-0" />
                  <span className="truncate font-medium">
                    From SMA Topic Library: "{selectedFromLibrary}" — all details below are fully editable.
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => setTopicWorkspaceMode('library')}
                  className="text-[11px] underline text-cinema-muted hover:text-white shrink-0 ml-2"
                >
                  Pick different
                </button>
              </div>
            )}

            {/* Topic Title */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                Topic Title *
              </label>
              <input
                type="text"
                placeholder="Enter topic title (e.g. Storytelling Through Design)..."
                value={form.topic}
                onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                required
                className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cinema-glow"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                Description / Learning Outcomes
              </label>
              <textarea
                rows={3}
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Overview of what attendees will learn in this session..."
                className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow resize-none"
              />
            </div>

            {/* Speaker, Date, Time & Duration */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                  Assigned Speaker
                </label>
                <div className="relative">
                  <select
                    value={form.speaker_id}
                    onChange={e => setForm(f => ({ ...f, speaker_id: e.target.value }))}
                    className="w-full appearance-none px-3 py-2 text-xs text-white bg-cinema-graphite border-b border-white/10 focus:outline-none focus:border-cinema-glow pr-8"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="">No speaker assigned (TBA)</option>
                    {speakers.map(s => (
                      <option key={s.id} value={s.id}>{s.name} {s.title ? `(${s.title})` : ''}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-cinema-muted/60 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                  Event Date *
                </label>
                <input
                  type="date"
                  required
                  value={form.event_date}
                  onChange={e => setForm(f => ({ ...f, event_date: e.target.value }))}
                  className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow"
                  style={{ colorScheme: 'dark' }}
                />
                {form.event_date && !isLastFridayOfMonth(form.event_date) && (
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <p className="text-[10px] text-[#EE8D21] font-sans">
                      Must be the last Friday of the month.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        const d = new Date(form.event_date)
                        const lastFri = getLastFridayOfMonth(d.getFullYear(), d.getMonth())
                        if (lastFri) setForm(f => ({ ...f, event_date: lastFri.toISOString().split('T')[0] }))
                      }}
                      className="text-[10px] text-cinema-glow hover:text-white font-sans underline shrink-0"
                    >
                      Use {formatDateLong(getLastFridayOfMonth(new Date(form.event_date).getFullYear(), new Date(form.event_date).getMonth())?.toISOString().split('T')[0] || '')}
                    </button>
                  </div>
                )}
                {form.event_date && isLastFridayOfMonth(form.event_date) && (
                  <p className="text-[10px] text-green-400 font-sans mt-1">
                    ✓ {formatDateLong(form.event_date)}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                  Start Time
                </label>
                <input
                  type="time"
                  value={form.start_time}
                  onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                  className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow"
                  style={{ colorScheme: 'dark' }}
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                  Duration (Minutes)
                </label>
                <input
                  type="number"
                  min={30}
                  max={480}
                  value={form.duration_minutes}
                  onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value, 10) || 120 }))}
                  className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow"
                />
              </div>
            </div>

            {/* Advanced Settings Accordion */}
            <div className="pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-xs text-cinema-glow hover:text-white font-sans inline-flex items-center gap-1.5 transition-colors"
              >
                {showAdvanced ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                {showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings (Venue, Capacity, Category, Requirements)'}
              </button>

              {showAdvanced && (
                <div className="grid sm:grid-cols-2 gap-4 mt-3 p-4 bg-white/[0.02] border border-white/5 text-xs">
                  <div className="sm:col-span-2">
                    <label className="block uppercase text-[10px] text-cinema-muted mb-1 font-sans">Venue Location</label>
                    <input
                      type="text"
                      value={form.venue}
                      onChange={e => setForm(f => ({ ...f, venue: e.target.value }))}
                      className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-1.5 text-xs text-white focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block uppercase text-[10px] text-cinema-muted mb-1 font-sans">Curriculum Category</label>
                    <input
                      type="text"
                      value={form.category}
                      onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                      className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-1.5 text-xs text-white focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block uppercase text-[10px] text-cinema-muted mb-1 font-sans">Capacity (Seats)</label>
                    <input
                      type="number"
                      placeholder="Unlimited"
                      value={form.capacity}
                      onChange={e => setForm(f => ({ ...f, capacity: e.target.value }))}
                      className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-1.5 text-xs text-white focus:outline-none"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block uppercase text-[10px] text-cinema-muted mb-1 font-sans">Requirements / Prerequisites</label>
                    <textarea
                      rows={2}
                      value={form.requirements}
                      onChange={e => setForm(f => ({ ...f, requirements: e.target.value }))}
                      placeholder="e.g. Bring a laptop with Lightroom installed..."
                      className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-1.5 text-xs text-white focus:outline-none resize-none"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="reg_open_chk"
                      checked={form.registration_open}
                      onChange={e => setForm(f => ({ ...f, registration_open: e.target.checked }))}
                      className="rounded border-white/20 bg-cinema-graphite text-cinema-glow"
                    />
                    <label htmlFor="reg_open_chk" className="text-xs text-white cursor-pointer font-sans">
                      Open for Public Registrations
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom Actions Bar */}
            <div className="flex items-center justify-between gap-3 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => setTopicWorkspaceMode(previousView || 'upcoming')}
                className="cinema-btn-ghost !px-4 !py-2 text-xs"
              >
                Cancel
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={saving || !form.topic.trim()}
                  className="cinema-btn-primary !px-6 !py-2 text-xs sm:text-sm font-semibold inline-flex items-center gap-1.5"
                >
                  {saving ? <LoadingSpinner size="sm" inline /> : form.id ? 'Save Changes' : 'Schedule Masterclass'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* ── Clone Modal ──────────────────────────────────────────────────────── */}
      {cloningModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-cinema-charcoal border border-white/10 p-6 space-y-4 shadow-2xl">
            <h3 className="font-display text-white text-lg font-bold">Clone Topic for New Date</h3>
            <p className="text-xs text-cinema-muted font-sans">
              Duplicate <strong className="text-white font-semibold">{cloningModal.topic}</strong> with a new date.
            </p>
            <div>
              <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                New Event Date *
              </label>
              <input
                type="date"
                value={cloneDate}
                onChange={e => setCloneDate(e.target.value)}
                className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow"
                style={{ colorScheme: 'dark' }}
              />
              <p className="text-[11px] text-cinema-muted/60 mt-1 font-sans">
                Suggested: last Friday of next month.
              </p>
            </div>
            <div className="flex gap-2 pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setCloningModal(null)}
                className="flex-1 cinema-btn-ghost !py-2 text-xs justify-center"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!cloneDate || cloning}
                onClick={handleClone}
                className="flex-1 cinema-btn-primary !py-2 text-xs justify-center"
              >
                {cloning ? <LoadingSpinner size="sm" inline /> : 'Clone Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Lightweight Topic Preview Modal (Read-Only Popover) ────────────────── */}
      {previewTopic && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewTopic(null)}
        >
          <div
            className="w-full max-w-lg bg-cinema-graphite border border-cinema-glow/30 p-5 space-y-4 shadow-2xl relative"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-white/10 pb-3">
              <div>
                <span className="text-[10px] uppercase font-bold tracking-wider text-cinema-gold px-2 py-0.5 bg-white/5 border border-white/10 font-sans">
                  {previewTopic.category}
                </span>
                <h4 className="font-display text-white text-base font-bold mt-1.5">
                  {previewTopic.title}
                </h4>
                <span className="text-xs text-cinema-muted font-mono">
                  {previewTopic.duration_minutes || 120} minutes
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPreviewTopic(null)}
                className="text-gray-400 hover:text-white p-1 rounded"
                aria-label="Close preview"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              <div>
                <h5 className="text-[11px] uppercase tracking-wider text-cinema-muted font-sans font-semibold mb-1">
                  Overview & Learning Outcomes
                </h5>
                <p className="text-xs text-cinema-soft/90 font-sans leading-relaxed">
                  {previewTopic.description}
                </p>
              </div>

              {previewTopic.suggested_takeaways && previewTopic.suggested_takeaways.length > 0 && (
                <div>
                  <h5 className="text-[11px] uppercase tracking-wider text-cinema-muted font-sans font-semibold mb-1.5">
                    Key Focus Areas
                  </h5>
                  <div className="space-y-1.5">
                    {previewTopic.suggested_takeaways.map((tk, i) => (
                      <div key={i} className="text-xs font-sans bg-white/[0.03] border border-white/5 p-2">
                        <strong className="text-cinema-glow">{tk.label}:</strong>{' '}
                        <span className="text-gray-300">{tk.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={() => setPreviewTopic(null)}
                className="flex-1 cinema-btn-ghost !py-2 text-xs justify-center"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  const item = previewTopic
                  setPreviewTopic(null)
                  handleSelectLibraryTopic(item)
                }}
                className="flex-1 cinema-btn-primary !py-2 text-xs justify-center font-semibold inline-flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                Add to Programme
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mark Finished Confirmation Modal ─────────────────────────────────── */}
      {finishConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-cinema-charcoal border border-white/10 p-6 space-y-4 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-green-500/10 border border-green-500/30 text-green-400 shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-white text-base font-bold">
                  Mark this Masterclass as finished?
                </h3>
                <p className="text-xs text-cinema-muted font-sans mt-1 leading-relaxed">
                  Finished sessions are moved to the historical archive, but all registrations, attendance, speaker, and certificate records are preserved.
                </p>
              </div>
            </div>

            <div className="p-3 bg-white/[0.03] border border-white/10 text-xs font-sans space-y-1">
              <div className="text-white font-semibold">{finishConfirmModal.topic}</div>
              <div className="text-cinema-muted text-[11px]">
                {new Date(finishConfirmModal.event_date).toLocaleDateString('en-UG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} • {finishConfirmModal.registration_count || 0} registered • {finishConfirmModal.checked_in_count || finishConfirmModal.attendees_count || 0} attended
              </div>
            </div>

            <div className="flex gap-2 pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setFinishConfirmModal(null)}
                className="flex-1 cinema-btn-ghost !py-2 text-xs justify-center"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={finishing}
                onClick={handleConfirmMarkFinished}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold text-xs transition-colors justify-center flex items-center gap-1.5"
              >
                {finishing ? <LoadingSpinner size="sm" inline /> : <CheckCircle2 size={13} />}
                Confirm & Mark Finished
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel Session Confirmation Modal ──────────────────────────────── */}
      {cancelConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-cinema-charcoal border border-white/10 p-6 space-y-4 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-red-500/10 border border-red-500/30 text-red-400 shrink-0">
                <XCircle className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-white text-base font-bold">
                  Cancel this Masterclass session?
                </h3>
                <p className="text-xs text-cinema-muted font-sans mt-1 leading-relaxed">
                  Cancelling removes the session from the upcoming schedule and closes registrations. All existing registrations are preserved. This action can be reversed by editing the session status back to "upcoming".
                </p>
              </div>
            </div>

            <div className="p-3 bg-white/[0.03] border border-white/10 text-xs font-sans space-y-1">
              <div className="text-white font-semibold">{cancelConfirmModal.topic}</div>
              <div className="text-cinema-muted text-[11px]">
                {new Date(cancelConfirmModal.event_date).toLocaleDateString('en-UG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} • {cancelConfirmModal.registration_count || 0} registered
              </div>
            </div>

            <div className="flex gap-2 pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setCancelConfirmModal(null)}
                className="flex-1 cinema-btn-ghost !py-2 text-xs justify-center"
              >
                Keep Session
              </button>
              <button
                type="button"
                disabled={cancelling}
                onClick={handleConfirmCancelSession}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold text-xs transition-colors justify-center flex items-center gap-1.5"
              >
                {cancelling ? <LoadingSpinner size="sm" inline /> : <XCircle size={13} />}
                Confirm & Cancel Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reopen Finished Session Confirmation Modal ──────────────────────── */}
      {reopenConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-cinema-charcoal border border-white/10 p-6 space-y-4 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-cinema-glow/10 border border-cinema-glow/30 text-cinema-glow shrink-0">
                <RotateCcw className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-white text-base font-bold">
                  Reopen this Masterclass?
                </h3>
                <p className="text-xs text-cinema-muted font-sans mt-1 leading-relaxed">
                  This will return the session to Upcoming so it can be managed as an active programme session again.
                </p>
              </div>
            </div>

            <div className="p-3 bg-white/[0.03] border border-white/10 text-xs font-sans space-y-3">
              <div>
                <div className="text-white font-semibold">{reopenConfirmModal.topic}</div>
                <div className="text-cinema-muted text-[11px] mt-0.5">
                  {new Date(reopenConfirmModal.event_date).toLocaleDateString('en-UG', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })} • {reopenConfirmModal.registration_count || 0} registrations
                </div>
              </div>

              <label className="flex items-start gap-2.5 pt-2.5 border-t border-white/10 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={reopenOpenRegistration}
                  onChange={(e) => setReopenOpenRegistration(e.target.checked)}
                  className="mt-0.5 rounded border-white/20 bg-cinema-graphite text-cinema-glow focus:ring-0 focus:ring-offset-0 cursor-pointer"
                />
                <div className="text-xs font-sans">
                  <span className="text-white font-medium block">
                    Open public registrations immediately
                  </span>
                  <span className="text-[11px] text-cinema-muted leading-tight block mt-0.5">
                    Leave unchecked to keep registration closed until you are ready to accept guests.
                  </span>
                </div>
              </label>
            </div>

            <div className="flex gap-2 pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setReopenConfirmModal(null)}
                className="flex-1 cinema-btn-ghost !py-2 text-xs justify-center"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={reopening}
                onClick={handleConfirmReopenSession}
                className="flex-1 cinema-btn-primary !py-2 text-xs justify-center font-semibold flex items-center gap-1.5"
              >
                {reopening ? <LoadingSpinner size="sm" inline /> : <RotateCcw size={13} />}
                Confirm & Reopen Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Regenerate Flyer Comparison Modal ─────────────────────────────────── */}
      {regenerateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm overflow-y-auto">
          <div className="w-full max-w-5xl bg-cinema-charcoal border border-white/10 shadow-2xl my-8">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-orange-400" />
                <h3 className="text-base font-bold text-white">Regenerate Flyer</h3>
                <span className="text-xs text-cinema-muted ml-2">
                  {regenerateModal.mc.speaker_name || regenerateModal.mc.topic}
                </span>
              </div>
              <button
                type="button"
                onClick={handleCloseRegenerate}
                className="cinema-btn-ghost !p-1.5 !min-h-0"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              {regenerateModal.loading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                  <LoadingSpinner size="md" />
                  <p className="text-sm text-cinema-muted">Generating alternative design…</p>
                </div>
              ) : regenerateModal.error ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <AlertCircle className="w-10 h-10 text-orange-400" />
                  <p className="text-sm text-white text-center max-w-md">{regenerateModal.error}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleTryAnother}
                      className="cinema-btn-ghost !py-2 !px-4 text-xs"
                    >
                      Try Another Template
                    </button>
                    <button
                      type="button"
                      onClick={handleKeepCurrent}
                      className="cinema-btn-primary !py-2 !px-4 text-xs"
                    >
                      Keep Current Design
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Current Design */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold tracking-wider text-cinema-muted uppercase">
                        Current Design
                      </span>
                      <span className="text-xs font-semibold text-white bg-white/10 px-2 py-0.5 rounded">
                        {LAYOUT_NAMES[regenerateModal.currentLayout]}
                      </span>
                      {regenerateModal.currentFont && FONT_PAIRINGS[regenerateModal.currentFont] && (
                        <span className="text-xs text-cinema-muted/70 italic">
                          {FONT_PAIRINGS[regenerateModal.currentFont]}
                        </span>
                      )}
                    </div>
                    {regenerateModal.currentPreview ? (
                      <img
                        src={regenerateModal.currentPreview}
                        alt="Current design"
                        className="w-full aspect-square object-contain bg-black/30 rounded border border-white/5"
                      />
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center bg-black/30 rounded border border-white/5">
                        <span className="text-xs text-cinema-muted">No preview</span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleKeepCurrent}
                      className="w-full cinema-btn-ghost !py-2.5 text-xs justify-center font-semibold"
                    >
                      Keep Current
                    </button>
                  </div>

                  {/* Alternative Design */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold tracking-wider text-orange-400 uppercase">
                        Alternative
                      </span>
                      <span className="text-xs font-semibold text-white bg-orange-400/20 text-orange-300 px-2 py-0.5 rounded">
                        {LAYOUT_NAMES[regenerateModal.altLayout]}
                      </span>
                      {regenerateModal.altFont && FONT_PAIRINGS[regenerateModal.altFont] && (
                        <span className="text-xs text-orange-300/70 italic">
                          {FONT_PAIRINGS[regenerateModal.altFont]}
                        </span>
                      )}
                    </div>
                    {regenerateModal.altPreview ? (
                      <img
                        src={regenerateModal.altPreview}
                        alt="Alternative design"
                        className="w-full aspect-square object-contain bg-black/30 rounded border border-orange-400/20"
                      />
                    ) : (
                      <div className="w-full aspect-square flex items-center justify-center bg-black/30 rounded border border-orange-400/20">
                        <span className="text-xs text-cinema-muted">No preview</span>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleTryAnother}
                        disabled={regenerateModal.loading}
                        className="flex-1 cinema-btn-ghost !py-2.5 text-xs justify-center"
                      >
                        Try Another
                      </button>
                      <button
                        type="button"
                        onClick={handleUseThisDesign}
                        disabled={savingTemplate}
                        className="flex-1 cinema-btn-primary !py-2.5 text-xs justify-center font-semibold flex items-center gap-1.5"
                      >
                        {savingTemplate ? <LoadingSpinner size="sm" inline /> : <Check size={13} />}
                        Use This Design
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Speakers View ────────────────────────────────────────────────────────────
function SpeakersView() {
  const [speakers, setSpeakers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showSpeakerModal, setShowSpeakerModal] = useState(false)
  const [editingSpeaker, setEditingSpeaker] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await masterclassService.getSpeakers()
      setSpeakers(res.speakers || [])
    } catch (err) {
      console.error(err)
      toast.error('Failed to load speakers')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleDelete = async (speakerId) => {
    if (!speakerId) return
    setDeleting(true)
    try {
      await masterclassService.adminDeleteSpeaker(speakerId)
      toast.success('Speaker removed successfully')
      setDeleteConfirm(null)
      load()
    } catch (err) {
      toast.error(err.message || 'Failed to remove speaker')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header & Add Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-white text-xl font-bold">Featured Speakers</h2>
          <p className="text-xs text-cinema-muted font-sans mt-0.5">
            Manage industry guest practitioners and masterclass instructors.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setEditingSpeaker(null); setShowSpeakerModal(true) }}
          className="cinema-btn-primary !px-4 !py-2.5 !min-h-0 text-xs sm:text-sm inline-flex items-center gap-1.5 shrink-0"
        >
          <Plus className="w-4 h-4" />
          Add Speaker
        </button>
      </div>

      {/* Speaker Cards Grid */}
      {loading ? (
        <LoadingSpinner className="py-16" />
      ) : speakers.length === 0 ? (
        <EmptyState
          icon={Mic}
          title="No speakers yet"
          description="Add your first featured speaker to associate them with upcoming topics and masterclasses."
          actionText="Add Speaker"
          onAction={() => { setEditingSpeaker(null); setShowSpeakerModal(true) }}
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {speakers.map((sp) => (
            <div
              key={sp.id}
              className="p-4 border border-white/10 bg-cinema-graphite/30 hover:bg-cinema-graphite/50 transition-colors flex flex-col justify-between space-y-4"
            >
              <div className="flex items-start gap-3">
                {sp.photo_url ? (
                  <img
                    src={resolveImageUrl(sp.photo_url)}
                    alt={sp.name}
                    className="w-14 h-14 object-cover border border-white/10 shrink-0"
                    style={{ objectPosition: getSpeakerObjectPosition(sp.name, 'thumb') }}
                  />
                ) : (
                  <div className="w-14 h-14 flex items-center justify-center bg-cinema-glow/10 border border-cinema-glow/20 shrink-0">
                    <Mic className="w-6 h-6 text-cinema-glow" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="font-sans text-white font-bold text-sm truncate">{sp.name}</h3>
                  <p className="text-cinema-muted text-xs truncate mt-0.5">{sp.title || sp.company || 'Industry Practitioner'}</p>
                  {sp.company && sp.title && <p className="text-cinema-muted/60 text-[11px] truncate">{sp.company}</p>}
                  <span className="inline-block mt-1 text-[10px] text-cinema-gold font-mono">
                    Rotation #{sp.order_index ?? 0}
                  </span>
                </div>
              </div>

              {sp.bio && (
                <p className="text-xs text-cinema-muted/70 font-sans line-clamp-2 leading-relaxed">
                  {sp.bio}
                </p>
              )}

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(sp)}
                  className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 text-xs transition-colors"
                  title="Remove Speaker"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => { setEditingSpeaker(sp); setShowSpeakerModal(true) }}
                  className="cinema-btn-ghost !px-3 !py-1.5 !min-h-0 text-xs inline-flex items-center gap-1"
                >
                  <Edit className="w-3 h-3" /> Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Speaker Form Modal with R2 Public Upload */}
      {showSpeakerModal && (
        <SpeakerModal
          speaker={editingSpeaker}
          onSave={async (payload) => {
            try {
              if (editingSpeaker?.id) {
                await masterclassService.adminUpdateSpeaker(editingSpeaker.id, payload)
                toast.success('Speaker updated!')
              } else {
                await masterclassService.adminCreateSpeaker(payload)
                toast.success('Speaker added!')
              }
              setShowSpeakerModal(false)
              setEditingSpeaker(null)
              load()
            } catch (err) {
              toast.error(err.message || 'Failed to save speaker')
            }
          }}
          onClose={() => { setShowSpeakerModal(false); setEditingSpeaker(null) }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="w-full max-w-md bg-cinema-charcoal border border-white/10 p-6 space-y-4 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/10 border border-red-500/30 text-red-400 shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-display text-white text-base font-bold">Remove Speaker?</h3>
                <p className="text-xs text-cinema-muted font-sans mt-0.5">
                  Confirm removing <strong className="text-white">{deleteConfirm.name}</strong> from the speakers directory.
                </p>
              </div>
            </div>
            <div className="flex gap-2 pt-2 border-t border-white/10">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 cinema-btn-ghost !py-2 text-xs justify-center"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => handleDelete(deleteConfirm.id)}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-semibold text-xs transition-colors justify-center flex items-center gap-1.5"
              >
                {deleting ? <LoadingSpinner size="sm" inline /> : <Trash2 size={13} />}
                Confirm Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Speaker Modal with Live Photo Preview ─────────────────────────────────────
function SpeakerModal({ speaker, onSave, onClose }) {
  const [form, setForm] = useState({
    id: speaker?.id || null,
    name: speaker?.name || '',
    title: speaker?.title || '',
    company: speaker?.company || '',
    bio: speaker?.bio || '',
    experience: speaker?.experience || '',
    specialization: speaker?.specialization || '',
    photo_url: speaker?.photo_url || '',
    photo_file: null,
    order_index: speaker?.order_index ?? 0,
    website_url: speaker?.website_url || '',
    linkedin_url: speaker?.linkedin_url || '',
  })
  const [preview, setPreview] = useState(speaker?.photo_url ? resolveImageUrl(speaker.photo_url) : '')
  const [saving, setSaving] = useState(false)

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0] || null
    setForm(f => ({ ...f, photo_file: file }))
    if (file) {
      setPreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) {
      toast.error('Speaker name is required.')
      return
    }
    setSaving(true)
    try {
      let payload = form
      if (form.photo_file) {
        payload = new FormData()
        for (const [key, value] of Object.entries(form)) {
          if (key === 'photo_file') {
            payload.append('photo_file', value)
          } else if (value !== null && value !== undefined) {
            payload.append(key, typeof value === 'boolean' ? String(value) : value)
          }
        }
      }
      await onSave(payload)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-cinema-charcoal border border-white/10 p-6 space-y-4 shadow-2xl relative max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <div>
            <h3 className="font-display text-white text-lg font-bold">
              {form.id ? 'Edit Speaker' : 'Add New Speaker'}
            </h3>
            <p className="text-xs text-cinema-muted font-sans mt-0.5">
              Speaker profile information and portrait photo.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Photo Upload with Live Thumbnail */}
          <div className="flex items-center gap-4 p-3 bg-white/[0.02] border border-white/10">
            {preview ? (
              <img src={preview} alt="Preview" className="w-16 h-16 object-cover border border-white/20 shrink-0" />
            ) : (
              <div className="w-16 h-16 flex items-center justify-center bg-cinema-glow/10 border border-cinema-glow/20 text-cinema-glow shrink-0">
                <Mic className="w-6 h-6" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                Portrait Photo (JPG, PNG, WebP)
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoSelect}
                className="text-xs text-gray-300 file:mr-2 file:py-1 file:px-2.5 file:border file:border-white/20 file:bg-white/10 file:text-white file:text-xs hover:file:bg-white/20 file:cursor-pointer"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
              Full Name *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. Mr. Herbert Mukkiriza"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                Professional Title / Role
              </label>
              <input
                type="text"
                placeholder="e.g. CEO & Lead Strategist"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                Company / Organization
              </label>
              <input
                type="text"
                placeholder="e.g. Synergy Multimedia"
                value={form.company}
                onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
              Short Biography
            </label>
            <textarea
              rows={3}
              placeholder="Speaker background and expertise..."
              value={form.bio}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow resize-none"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                Rotation Order Index
              </label>
              <input
                type="number"
                min={0}
                value={form.order_index}
                onChange={e => setForm(f => ({ ...f, order_index: parseInt(e.target.value, 10) || 0 }))}
                className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-cinema-muted font-sans mb-1 font-medium">
                LinkedIn Profile URL
              </label>
              <input
                type="url"
                placeholder="https://linkedin.com/in/..."
                value={form.linkedin_url}
                onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))}
                className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white focus:outline-none focus:border-cinema-glow"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-3 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 cinema-btn-ghost !py-2.5 text-xs justify-center"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim()}
              className="flex-1 cinema-btn-primary !py-2.5 text-xs justify-center"
            >
              {saving ? <LoadingSpinner size="sm" inline /> : form.id ? 'Save Changes' : 'Add Speaker'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Registrations ──────────────────────────────────────────────────────────
function DeleteRow({ label, present }) {
  return (
    <div className="flex items-center gap-2">
      <span className={present ? 'text-red-300' : 'text-cinema-muted/40'}>
        {present ? '✓' : '○'}
      </span>
      <span className={present ? 'text-red-200' : 'text-cinema-muted/40 line-through'}>
        {label}
      </span>
      {!present && <span className="text-cinema-muted/30 text-[10px]">(none)</span>}
    </div>
  )
}

function RegistrationsTab({ onNavigateVerifications, onNavigateCertificates, initialMasterclassId }) {
  const { user } = useAuthStore()
  const canDelete = user?.user_type === 'admin'

  const [registrations, setRegistrations] = useState([])
  const [masterclasses, setMasterclasses] = useState([])
  const [search, setSearch] = useState('')
  const [filterMC, setFilterMC] = useState(initialMasterclassId ? String(initialMasterclassId) : '')
  const [filterPayment, setFilterPayment] = useState('')
  const [showDeletedOnly, setShowDeletedOnly] = useState(false)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [actionLoadingId, setActionLoadingId] = useState(null)
  const [previewReg, setPreviewReg] = useState(null)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(null)
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null)
  const [permanentDeletePreview, setPermanentDeletePreview] = useState(null)
  const [permanentDeletePreviewLoading, setPermanentDeletePreviewLoading] = useState(false)
  const [permanentDeleteConfirmText, setPermanentDeleteConfirmText] = useState('')
  const [permanentDeleting, setPermanentDeleting] = useState(false)

  useEffect(() => {
    if (initialMasterclassId) {
      setFilterMC(String(initialMasterclassId))
    }
  }, [initialMasterclassId])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [rRes, mRes] = await Promise.all([
        masterclassService.adminGetRegistrations({
          masterclass_id: filterMC || undefined,
          search: search || undefined,
          payment_status: filterPayment || undefined,
          deleted_only: showDeletedOnly ? 'true' : undefined,
        }),
        masterclassService.adminGetMasterclasses(),
      ])
      setRegistrations(rRes.registrations || [])
      setMasterclasses(mRes.masterclasses || [])
    } catch (err) {
      console.error(err)
      toast.error('Failed to load registrations')
    } finally { setLoading(false) }
  }, [filterMC, search, filterPayment, showDeletedOnly])

  useEffect(() => { load() }, [load])

  const handleSearch = (e) => { e.preventDefault(); load() }

  const handleExport = async () => {
    setExporting(true)
    try {
      const data = await masterclassService.adminExport(filterMC || undefined)
      ExportCSV(data.rows || [])
    } catch (err) { console.error(err) } finally { setExporting(false) }
  }

  const handleDelete = async (registrationId) => {
    if (!registrationId) return
    setDeleting(true)
    try {
      const res = await masterclassService.adminDeleteRegistration(registrationId)
      toast.success(res.message || 'Registration deleted successfully')
      setShowDeleteConfirm(null)
      load()
    } catch (err) {
      console.error(err)
      toast.error(err.message || 'Failed to delete registration')
    } finally {
      setDeleting(false)
    }
  }

  const handleRestore = async (reg) => {
    if (!reg?.id) return
    setActionLoadingId(reg.id)
    try {
      const res = await masterclassService.adminRestoreRegistration(reg.id)
      toast.success(res.message || 'Registration restored successfully')
      setShowRestoreConfirm(null)
      load()
    } catch (err) {
      console.error(err)
      toast.error(err.message || 'Failed to restore registration')
    } finally {
      setActionLoadingId(null)
    }
  }

  const openPermanentDelete = async (reg) => {
    setPermanentDeleteTarget(reg)
    setPermanentDeletePreview(null)
    setPermanentDeleteConfirmText('')
    setPermanentDeletePreviewLoading(true)
    try {
      const preview = await masterclassService.adminPermanentDeletePreview(reg.id)
      setPermanentDeletePreview(preview)
    } catch (err) {
      console.error(err)
      toast.error(err.message || 'Failed to load deletion preview')
      setPermanentDeleteTarget(null)
    } finally {
      setPermanentDeletePreviewLoading(false)
    }
  }

  const closePermanentDelete = () => {
    setPermanentDeleteTarget(null)
    setPermanentDeletePreview(null)
    setPermanentDeleteConfirmText('')
    setPermanentDeleting(false)
  }

  const handlePermanentDelete = async () => {
    if (!permanentDeleteTarget?.id) return
    if (permanentDeleteConfirmText !== permanentDeleteTarget.registration_number) return
    setPermanentDeleting(true)
    try {
      const res = await masterclassService.adminPermanentDeleteRegistration(permanentDeleteTarget.id)
      toast.success(res.message || 'Registration permanently deleted')
      closePermanentDelete()
      load()
    } catch (err) {
      console.error(err)
      toast.error(err.message || 'Failed to permanently delete registration')
    } finally {
      setPermanentDeleting(false)
    }
  }

  const handleCopyRegNumber = (regNumber) => {
    if (!regNumber) return
    navigator.clipboard?.writeText(regNumber).then(() => {
      toast.success('Registration number copied')
    }).catch(() => {
      toast.error('Failed to copy')
    })
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col gap-3">
        <form onSubmit={handleSearch} className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted/60" />
          <input type="text" placeholder="Search name, email, reg #…" value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 font-sans text-sm text-white placeholder-cinema-muted/60 bg-cinema-graphite border-b border-white/10 focus:outline-none focus:border-cinema-glow/50"
            style={{ fontSize: 16 }} />
        </form>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <select value={filterMC} onChange={e => setFilterMC(e.target.value)}
              className="w-full appearance-none pl-4 pr-10 py-3 font-sans text-sm text-white bg-cinema-graphite border-b border-white/10 focus:outline-none focus:border-cinema-glow"
              style={{ colorScheme: 'dark' }}>
              <option value="">All Sessions</option>
              {masterclasses.map(m => (
                <option key={m.id} value={m.id}>{m.topic.substring(0, 35)}…</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted/60 pointer-events-none" />
          </div>

          <div className="relative flex-1">
            <select value={filterPayment} onChange={e => setFilterPayment(e.target.value)}
              className="w-full appearance-none pl-4 pr-10 py-3 font-sans text-sm text-white bg-cinema-graphite border-b border-white/10 focus:outline-none focus:border-cinema-glow"
              style={{ colorScheme: 'dark' }}>
              <option value="">All Payment States</option>
              <option value="COMPLETED">Paid (Completed)</option>
              <option value="PENDING_VERIFICATION">Proof Submitted (Pending Review)</option>
              <option value="REJECTED">Proof Rejected</option>
              <option value="PENDING">Payment Pending (No Proof)</option>
              <option value="NOT_REQUIRED">Free / Not Required</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted/60 pointer-events-none" />
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {canDelete && (
            <label className="flex items-center gap-2 cursor-pointer text-xs font-sans px-3 py-2.5 bg-cinema-graphite border-b border-white/10 text-cinema-muted hover:text-white transition-colors select-none">
              <input
                type="checkbox"
                checked={showDeletedOnly}
                onChange={(e) => setShowDeletedOnly(e.target.checked)}
                className="accent-red-500 rounded-none w-3.5 h-3.5 cursor-pointer"
              />
              <span>Show Deleted</span>
            </label>
          )}

          <div className="flex gap-2 ml-auto">
            <button onClick={load} className="cinema-btn-ghost !p-3 !min-h-0" title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>

            <button onClick={handleExport} disabled={exporting}
              className="cinema-btn-primary justify-center !px-4 !py-3 !min-h-0 text-sm">
              {exporting ? <LoadingSpinner size="sm" inline /> : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline">Export CSV</span>
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? <LoadingSpinner className="py-16" /> : (
        <>
          {/* Desktop table */}
          <div className="hidden lg:block overflow-x-auto border border-white/10">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-white/5">
                  {['Reg #', 'Name', 'Email', 'Phone', 'Course', 'Entitlement', 'Payment Status', 'Checked In', 'Date', 'Actions'].map(h => (
                    <th key={h} className={`px-4 py-3.5 text-cinema-gold text-xs uppercase tracking-[0.14em] font-sans font-semibold ${h === 'Actions' ? 'text-right' : 'text-left'}`}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {registrations.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-cinema-muted/60 font-sans text-sm">
                      {showDeletedOnly ? 'No deleted registrations found' : 'No registrations found'}
                    </td>
                  </tr>
                ) : registrations.map(r => (
                  <tr key={r.id} className={`table-row border-b border-white/5 transition-colors ${r.deleted_at ? 'bg-red-950/10 opacity-75' : 'hover:bg-white/[0.02]'}`}>
                    <td className="px-4 py-3.5 font-sans text-xs text-cinema-glow font-mono whitespace-nowrap">
                      {r.registration_number}
                      {r.deleted_at && (
                        <span className="block text-[9px] uppercase tracking-wider text-red-400 font-sans mt-0.5">
                          Deleted {new Date(r.deleted_at).toLocaleDateString()}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 font-sans text-sm text-white font-medium whitespace-nowrap">{r.first_name} {r.last_name}</td>
                    <td className="px-4 py-3.5 font-sans text-xs text-cinema-muted">{r.email}</td>
                    <td className="px-4 py-3.5 font-sans text-xs text-cinema-muted">{r.phone || '—'}</td>
                    <td className="px-4 py-3.5 font-sans text-xs text-cinema-muted">{r.current_course || '—'}</td>
                    <td className="px-4 py-3.5">
                      {r.entitlement_type === 'FREE' ? (
                        <span className="font-sans text-xs text-green-400 font-medium">Free (Student)</span>
                      ) : (
                        <span className="font-sans text-xs text-[#EE8D21] font-medium">Guest Pass (150k)</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {r.payment_status === 'COMPLETED' ? (
                        <span className="inline-flex items-center gap-1 text-green-400 font-sans text-xs font-semibold">
                          <Check className="w-3 h-3" /> Paid
                        </span>
                      ) : r.payment_status === 'PENDING_VERIFICATION' ? (
                        <button
                          onClick={() => onNavigateVerifications && onNavigateVerifications()}
                          className="inline-flex items-center gap-1 text-sky-400 bg-sky-500/10 border border-sky-500/30 px-2 py-0.5 font-sans text-xs font-semibold hover:bg-sky-500/20 transition-colors"
                          title="Click to review payment proof in Verifications tab"
                        >
                          <Clock className="w-3 h-3" /> Verify Proof
                        </button>
                      ) : r.payment_status === 'REJECTED' ? (
                        <span className="inline-flex items-center gap-1 text-red-400 font-sans text-xs font-semibold" title={r.rejection_reason || 'Proof rejected'}>
                          <XCircle className="w-3 h-3" /> Proof Rejected
                        </span>
                      ) : r.payment_status === 'PENDING' ? (
                        <span className="inline-flex items-center gap-1 text-[#EE8D21] font-sans text-xs font-semibold">
                          <Clock className="w-3 h-3" /> Pending (150k)
                        </span>
                      ) : (
                        <span className="text-cinema-muted/50 font-sans text-xs">Free / N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      {r.checked_in ? (
                        <span className="flex items-center gap-1.5 text-green-400 font-sans text-xs"><CheckCircle2 className="w-3.5 h-3.5" /> In</span>
                      ) : (
                        <span className="text-cinema-muted/50 font-sans text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 font-sans text-xs text-cinema-muted/60 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3.5 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setPreviewReg(r)}
                          className="inline-flex items-center justify-center p-1.5 text-cinema-glow hover:text-cinema-gold bg-cinema-glow/10 border border-cinema-glow/20 hover:border-cinema-glow/40 hover:bg-cinema-glow/20 font-sans transition-colors cursor-pointer"
                          title={`Preview event pass for ${r.registration_number}`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyRegNumber(r.registration_number)}
                          className="inline-flex items-center justify-center p-1.5 text-cinema-soft/70 hover:text-white bg-white/5 border border-white/10 hover:border-white/20 hover:bg-white/10 font-sans transition-colors cursor-pointer"
                          title={`Copy registration number ${r.registration_number}`}
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        {r.checked_in && onNavigateCertificates && (
                          <button
                            type="button"
                            onClick={() => onNavigateCertificates(r.email)}
                            className="inline-flex items-center justify-center p-1.5 text-cinema-glow hover:text-cinema-gold bg-cinema-glow/10 border border-cinema-glow/20 hover:border-cinema-glow/40 hover:bg-cinema-glow/20 font-sans transition-colors cursor-pointer"
                            title={`View all certificates for ${r.email}`}
                          >
                            <Award className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canDelete && (
                          r.deleted_at ? (
                            <>
                              <button
                                type="button"
                                onClick={() => setShowRestoreConfirm(r)}
                                disabled={actionLoadingId === r.id}
                                className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-1 font-sans font-medium transition-colors"
                                title={`Restore ${r.registration_number}`}
                              >
                                {actionLoadingId === r.id ? <LoadingSpinner size="sm" inline /> : <RotateCcw className="w-3.5 h-3.5" />}
                                Restore
                              </button>
                              <button
                                type="button"
                                onClick={() => openPermanentDelete(r)}
                                disabled={actionLoadingId === r.id}
                                className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-white bg-red-600/20 border border-red-600/40 hover:border-red-600/60 hover:bg-red-600/40 px-2.5 py-1 font-sans font-medium transition-colors"
                                title={`Permanently delete ${r.registration_number}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                Permanent
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setShowDeleteConfirm(r)}
                              disabled={actionLoadingId === r.id}
                              className="inline-flex items-center justify-center p-1.5 text-red-400 hover:text-red-300 bg-red-500/10 border border-red-500/20 hover:border-red-500/40 hover:bg-red-500/20 font-sans transition-colors cursor-pointer"
                              title={`Delete ${r.registration_number}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="lg:hidden space-y-3">
            {registrations.length === 0 ? (
              <div className="border border-white/10 p-6 text-center text-cinema-muted/60 font-sans text-sm">
                {showDeletedOnly ? 'No deleted registrations found' : 'No registrations found'}
              </div>
            ) : registrations.map(r => (
              <div key={r.id} className={`border border-white/10 p-3.5 space-y-2.5 ${r.deleted_at ? 'bg-red-950/10 opacity-75' : 'bg-cinema-graphite/20'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-sans text-sm text-white font-medium truncate">{r.first_name} {r.last_name}</p>
                    <p className="font-mono text-xs text-cinema-glow">{r.registration_number}</p>
                    {r.deleted_at && <p className="text-[10px] uppercase tracking-wider text-red-400 font-sans mt-0.5">Deleted {new Date(r.deleted_at).toLocaleDateString()}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    {r.entitlement_type === 'FREE' ? (
                      <span className="font-sans text-xs text-green-400 font-medium">Free</span>
                    ) : (
                      <span className="font-sans text-xs text-[#EE8D21] font-medium">Guest Pass</span>
                    )}
                    {r.checked_in ? (
                      <span className="flex items-center gap-1 text-green-400 font-sans text-xs"><CheckCircle2 className="w-3 h-3" /> In</span>
                    ) : (
                      <span className="text-cinema-muted/50 font-sans text-xs">Not checked in</span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs font-sans">
                  <div className="col-span-2"><span className="text-cinema-muted/60">Email:</span> <span className="text-cinema-muted truncate block">{r.email}</span></div>
                  <div><span className="text-cinema-muted/60">Phone:</span> <span className="text-cinema-muted">{r.phone || '—'}</span></div>
                  <div><span className="text-cinema-muted/60">Course:</span> <span className="text-cinema-muted">{r.current_course || '—'}</span></div>
                  <div><span className="text-cinema-muted/60">Date:</span> <span className="text-cinema-muted">{new Date(r.created_at).toLocaleDateString()}</span></div>
                  <div><span className="text-cinema-muted/60">Payment:</span>{' '}
                    {r.payment_status === 'COMPLETED' ? (
                      <span className="text-green-400 font-semibold">Paid</span>
                    ) : r.payment_status === 'PENDING_VERIFICATION' ? (
                      <button onClick={() => onNavigateVerifications && onNavigateVerifications()} className="text-sky-400 bg-sky-500/10 border border-sky-500/30 px-1.5 py-0.5 font-semibold">Verify Proof</button>
                    ) : r.payment_status === 'REJECTED' ? (
                      <span className="text-red-400 font-semibold">Rejected</span>
                    ) : r.payment_status === 'PENDING' ? (
                      <span className="text-[#EE8D21] font-semibold">Pending</span>
                    ) : (
                      <span className="text-cinema-muted/50">N/A</span>
                    )}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1.5 pt-1 border-t border-white/5">
                  <button type="button" onClick={() => setPreviewReg(r)} className="inline-flex items-center justify-center p-1.5 text-cinema-glow hover:text-cinema-gold bg-cinema-glow/10 border border-cinema-glow/20" title="Preview pass"><Eye className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => handleCopyRegNumber(r.registration_number)} className="inline-flex items-center justify-center p-1.5 text-cinema-soft/70 hover:text-white bg-white/5 border border-white/10" title="Copy reg #"><Copy className="w-3.5 h-3.5" /></button>
                  {r.checked_in && onNavigateCertificates && (
                    <button type="button" onClick={() => onNavigateCertificates(r.email)} className="inline-flex items-center justify-center p-1.5 text-cinema-glow hover:text-cinema-gold bg-cinema-glow/10 border border-cinema-glow/20" title="Certificates"><Award className="w-3.5 h-3.5" /></button>
                  )}
                  <div className="flex-1" />
                  {canDelete && (
                    r.deleted_at ? (
                      <>
                        <button type="button" onClick={() => setShowRestoreConfirm(r)} disabled={actionLoadingId === r.id} className="inline-flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1 font-medium">
                          {actionLoadingId === r.id ? <LoadingSpinner size="sm" inline /> : <RotateCcw className="w-3.5 h-3.5" />} Restore
                        </button>
                        <button type="button" onClick={() => openPermanentDelete(r)} disabled={actionLoadingId === r.id} className="inline-flex items-center gap-1 text-xs text-red-300 bg-red-600/20 border border-red-600/40 px-2 py-1 font-medium">
                          <Trash2 className="w-3.5 h-3.5" /> Perm
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => setShowDeleteConfirm(r)} disabled={actionLoadingId === r.id} className="inline-flex items-center justify-center p-1.5 text-red-400 hover:text-red-300 bg-red-500/10 border border-red-500/20" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer summary */}
          <div className="p-3 bg-cinema-graphite/40 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
            <p className="text-cinema-muted/60 font-sans text-xs">
              {showDeletedOnly
                ? `${registrations.length} deleted registration${registrations.length !== 1 ? 's' : ''}`
                : `${registrations.length} active registration${registrations.length !== 1 ? 's' : ''}`}
            </p>
            {showDeletedOnly && (
              <p className="text-[11px] text-amber-400/80 font-sans flex items-center gap-1">
                <AlertTriangle size={12} /> Deleted — excluded from active reports. Use Permanent Delete to remove permanently.
              </p>
            )}
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-cinema-charcoal p-6 max-w-md w-full border border-white/10 shadow-2xl space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-500/10 border border-red-500/30 text-red-400 shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-white font-sans">Delete Masterclass Registration?</h3>
                <p className="text-xs text-cinema-muted font-sans mt-0.5">
                  Confirm deletion for the following attendee:
                </p>
              </div>
            </div>

            <div className="p-3.5 bg-cinema-graphite border border-white/10 text-xs font-sans space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-cinema-muted">Attendee:</span>
                <span className="text-white font-semibold">{showDeleteConfirm.first_name} {showDeleteConfirm.last_name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-cinema-muted">Registration #:</span>
                <span className="text-cinema-glow font-mono font-bold">{showDeleteConfirm.registration_number}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-cinema-muted">Email:</span>
                <span className="text-gray-300 font-mono text-[11px]">{showDeleteConfirm.email}</span>
              </div>
              {showDeleteConfirm.topic && (
                <div className="pt-1.5 border-t border-white/5 flex items-start justify-between gap-2">
                  <span className="text-cinema-muted shrink-0">Session:</span>
                  <span className="text-gray-300 text-right truncate">{showDeleteConfirm.topic}</span>
                </div>
              )}
            </div>

            <div className="p-3 bg-amber-950/20 border border-amber-900/30 text-[11px] text-amber-200/90 font-sans leading-relaxed">
              <strong className="text-white block mb-0.5">Remove Registration?</strong>
              This will remove the registration from the active registration list. The registration can still be restored later from the Deleted Registrations view.
              {showDeleteConfirm.payment_status === 'COMPLETED' && (
                <span className="block mt-1 text-amber-300">Note: This attendee has a completed payment. The financial ledger entry will be preserved for audit compliance.</span>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setShowDeleteConfirm(null)}
                className="cinema-btn-ghost !px-4 !py-2 text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => handleDelete(showDeleteConfirm.id)}
                className="px-4 py-2 text-xs bg-red-600 hover:bg-red-700 text-white font-semibold font-sans transition-colors flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
              >
                {deleting ? <LoadingSpinner size="sm" inline /> : <Trash2 size={13} />}
                Remove Registration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-cinema-charcoal p-6 max-w-md w-full border border-white/10 shadow-2xl space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 shrink-0">
                <RotateCcw className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-white font-sans">Restore Registration?</h3>
                <p className="text-xs text-cinema-muted font-sans mt-0.5">
                  This will return the registration to the active registration list.
                </p>
              </div>
            </div>

            <div className="p-3.5 bg-cinema-graphite border border-white/10 text-xs font-sans space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-cinema-muted">Attendee:</span>
                <span className="text-white font-semibold">{showRestoreConfirm.first_name} {showRestoreConfirm.last_name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-cinema-muted">Registration #:</span>
                <span className="text-cinema-glow font-mono font-bold">{showRestoreConfirm.registration_number}</span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
              <button
                type="button"
                disabled={actionLoadingId === showRestoreConfirm.id}
                onClick={() => setShowRestoreConfirm(null)}
                className="cinema-btn-ghost !px-4 !py-2 text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={actionLoadingId === showRestoreConfirm.id}
                onClick={() => handleRestore(showRestoreConfirm)}
                className="px-4 py-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold font-sans transition-colors flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
              >
                {actionLoadingId === showRestoreConfirm.id ? <LoadingSpinner size="sm" inline /> : <RotateCcw size={13} />}
                Restore Registration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {permanentDeleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-cinema-charcoal max-w-lg w-full border border-red-600/40 shadow-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-5 border-b border-red-600/30 bg-red-950/20 flex items-start gap-3 shrink-0">
              <div className="p-2 bg-red-500/20 border border-red-500/40 text-red-400 shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-red-300 font-sans">Permanently Delete Registration</h3>
                <p className="text-xs text-red-200/70 font-sans mt-0.5">This action cannot be undone.</p>
              </div>
            </div>

            {/* Body — scrollable */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {permanentDeletePreviewLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner className="py-4" />
                  <p className="text-cinema-muted text-sm font-sans ml-3">Loading deletion summary…</p>
                </div>
              ) : permanentDeletePreview ? (
                <>
                  {/* Registration details */}
                  <div className="space-y-2">
                    <div className="p-3 bg-cinema-graphite border border-white/10 text-xs font-sans space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-cinema-muted">Registration:</span>
                        <span className="text-cinema-glow font-mono font-bold">{permanentDeletePreview.registration.registration_number}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-cinema-muted">Attendee:</span>
                        <span className="text-white font-semibold">{permanentDeletePreview.registration.first_name} {permanentDeletePreview.registration.last_name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-cinema-muted">Masterclass:</span>
                        <span className="text-gray-300 text-right truncate">{permanentDeletePreview.masterclass.topic}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-cinema-muted">Date:</span>
                        <span className="text-gray-300">
                          {permanentDeletePreview.masterclass.event_date
                            ? new Date(permanentDeletePreview.masterclass.event_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
                            : '—'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Warning: invoice shared with student */}
                  {permanentDeletePreview.warnings?.invoice_shared_with_student && (
                    <div className="p-3 bg-amber-950/30 border border-amber-700/40 text-[11px] text-amber-200 font-sans leading-relaxed">
                      <strong className="text-amber-300 block mb-0.5">Warning: Shared Invoice</strong>
                      This registration's invoice is linked to a student enrollment and cannot be safely deleted. Permanent deletion will be blocked. Please contact technical support if you need to remove this registration.
                    </div>
                  )}

                  {/* What will be removed */}
                  <div className="space-y-1.5">
                    <p className="text-xs text-white font-sans font-semibold uppercase tracking-wider">The following records will be removed:</p>
                    <div className="p-3 bg-red-950/15 border border-red-900/30 text-xs font-sans space-y-1">
                      <DeleteRow label="Registration" present={permanentDeletePreview.will_delete.registration} />
                      <DeleteRow label="Payment records" present={permanentDeletePreview.will_delete.payment} />
                      <DeleteRow label="Invoice records" present={permanentDeletePreview.will_delete.invoice} />
                      <DeleteRow label="Certificate record" present={permanentDeletePreview.will_delete.certificate} />
                      <DeleteRow label={`QR / check-in records${permanentDeletePreview.will_delete.qr_scans ? ` (${permanentDeletePreview.will_delete.qr_scans})` : ''}`} present={permanentDeletePreview.will_delete.qr_scans > 0} />
                      <DeleteRow label={`Reminder records${permanentDeletePreview.will_delete.reminder_logs ? ` (${permanentDeletePreview.will_delete.reminder_logs})` : ''}`} present={permanentDeletePreview.will_delete.reminder_logs > 0} />
                      <DeleteRow label="Private payment proof" present={permanentDeletePreview.will_delete.payment_proof} />
                    </div>
                  </div>

                  {/* What will be preserved */}
                  <div className="space-y-1.5">
                    <p className="text-xs text-cinema-muted font-sans">The deletion will be recorded in the administrative audit log. The following will be preserved:</p>
                    <div className="p-3 bg-cinema-graphite/50 border border-white/5 text-[11px] text-cinema-muted font-sans space-y-0.5">
                      <div>✓ Audit log record</div>
                      <div>✓ Advisor lead activity history</div>
                      <div>✓ Student badges</div>
                      <div>✓ Monthly attendance certificates</div>
                    </div>
                  </div>

                  {/* Metric impact */}
                  <div className="p-3 bg-cinema-graphite border border-white/10 text-[11px] text-cinema-muted font-sans leading-relaxed">
                    The registration will be removed from registration and attendance metrics.
                  </div>

                  {/* Type-to-confirm */}
                  <div className="space-y-2 pt-2 border-t border-white/10">
                    <p className="text-xs text-white font-sans font-semibold">
                      Type the registration number to confirm:
                    </p>
                    <p className="text-cinema-glow font-mono text-sm font-bold">{permanentDeletePreview.registration.registration_number}</p>
                    <input
                      type="text"
                      value={permanentDeleteConfirmText}
                      onChange={(e) => setPermanentDeleteConfirmText(e.target.value.toUpperCase())}
                      placeholder="Type registration number here"
                      autoFocus
                      autoComplete="off"
                      className="w-full px-4 py-3 font-mono text-sm text-white placeholder-cinema-muted/40 bg-cinema-graphite border border-white/10 focus:outline-none focus:border-red-500/50"
                      style={{ fontSize: 16 }}
                    />
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-cinema-muted text-sm font-sans">
                  Failed to load deletion preview. Please close and try again.
                </div>
              )}
            </div>

            {/* Footer — fixed */}
            <div className="p-4 border-t border-white/10 bg-cinema-charcoal shrink-0 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={permanentDeleting}
                onClick={closePermanentDelete}
                className="cinema-btn-ghost !px-4 !py-2 text-xs"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  permanentDeleting ||
                  permanentDeletePreviewLoading ||
                  !permanentDeletePreview ||
                  permanentDeleteConfirmText !== permanentDeleteTarget.registration_number ||
                  permanentDeletePreview?.warnings?.invoice_shared_with_student
                }
                onClick={handlePermanentDelete}
                className="px-4 py-2 text-xs bg-red-700 hover:bg-red-800 text-white font-semibold font-sans transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                {permanentDeleting ? <LoadingSpinner size="sm" inline /> : <Trash2 size={13} />}
                Permanently Delete Registration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pass Preview Modal ── */}
      {previewReg && (
        <PassPreviewModal
          registration={previewReg}
          onClose={() => setPreviewReg(null)}
        />
      )}
    </div>
  )
}

// ── Pass Preview Modal (admin read-only pass inspection) ────────────────────
function PassPreviewModal({ registration, onClose }) {
  const modalRef = useRef(null)
  const [downloading, setDownloading] = useState(null)

  const handleDownloadPDF = async () => {
    setDownloading('pdf')
    try {
      const passEl = modalRef.current?.querySelector('.digital-pass-export')
      if (!passEl) throw new Error('Pass element not found')
      await exportElementPDF(
        passEl,
        `SMA-Pass-${registration.registration_number || 'PASS'}.pdf`,
        { orientation: 'portrait', format: [85, 135], backgroundColor: '#ffffff' },
      )
    } catch (err) {
      console.error('PDF download failed', err)
      toast.error('PDF download failed')
    } finally {
      setDownloading(null)
    }
  }

  const handleDownloadPNG = async () => {
    setDownloading('png')
    try {
      const passEl = modalRef.current?.querySelector('.digital-pass-export')
      if (!passEl) throw new Error('Pass element not found')
      await exportElementPNG(
        passEl,
        `SMA-Pass-${registration.registration_number || 'PASS'}.png`,
        { scale: 3, backgroundColor: '#ffffff' },
      )
    } catch (err) {
      console.error('PNG download failed', err)
      toast.error('PNG download failed')
    } finally {
      setDownloading(null)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div ref={modalRef} className="relative w-full max-w-[520px] my-4 sm:my-8">
        {/* Close button — positioned above the pass on mobile, top-right on desktop */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div>
            <h3 className="font-display text-white text-base font-bold">Digital Event Pass Preview</h3>
            <p className="text-cinema-muted/70 font-sans text-xs mt-0.5">
              Read-only preview · {registration.first_name} {registration.last_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center w-9 h-9 text-gray-400 hover:text-white bg-white/5 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
            title="Close preview"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* The pass itself — rendered in preview mode (no payment controls, no action grid) */}
        <div className="max-h-[65vh] overflow-y-auto sm:overflow-visible">
          <DigitalEventPass registration={registration} mode="preview" />
        </div>

        {/* Admin action buttons — outside the pass, not included in exports */}
        <div className="mt-4 sm:mt-5 flex flex-col sm:flex-row gap-2 print:hidden">
          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={downloading === 'pdf'}
            className="cinema-btn-primary flex-1 justify-center !px-4 !py-3 !min-h-0 text-sm disabled:opacity-50"
          >
            {downloading === 'pdf' ? <LoadingSpinner size="sm" inline /> : <Download className="w-4 h-4" />}
            Download PDF
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="cinema-btn-ghost flex-1 justify-center !px-4 !py-3 !min-h-0 text-sm"
          >
            <FileText className="w-4 h-4" />
            Print
          </button>
          <button
            type="button"
            onClick={handleDownloadPNG}
            disabled={downloading === 'png'}
            className="cinema-btn-ghost flex-1 justify-center !px-4 !py-3 !min-h-0 text-sm disabled:opacity-50"
          >
            {downloading === 'png' ? <LoadingSpinner size="sm" inline /> : <Download className="w-4 h-4" />}
            Download PNG
          </button>
          <button
            type="button"
            onClick={onClose}
            className="cinema-btn-ghost flex-1 justify-center !px-4 !py-3 !min-h-0 text-sm"
          >
            <X className="w-4 h-4" />
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
function CheckInTab() {
  const [regNum, setRegNum] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [checkedIn, setCheckedIn] = useState(false)
  const [error, setError] = useState('')
  const [todayCount, setTodayCount] = useState(null)
  const [collectPayment, setCollectPayment] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [txRef, setTxRef] = useState('')
  const didScanRef = useRef(false)

  useEffect(() => {
    masterclassService.adminGetStats().then(d => setTodayCount(d.stats)).catch(() => {})
  }, [checkedIn])

  // Auto-verify from a scanned admin QR URL (e.g. /masterclass/admin?scan=SMA-MC-...)
  useEffect(() => {
    if (didScanRef.current) return
    const scan = new URLSearchParams(window.location.search).get('scan')
    if (!scan) return
    didScanRef.current = true
    verifyFromNumber(scan)
  }, [])

  function extractRegNumber(raw) {
    const s = (raw || '').trim()
    if (!s) return ''
    try {
      const u = new URL(s)
      const entries = Array.from(u.searchParams.entries())
      const getParam = (key) => entries.find(([k]) => k.toLowerCase() === key)?.[1]
      return getParam('scan') || getParam('registration') || getParam('reg') || s
    } catch {
      return s
    }
  }

  const getIsGuestPending = (reg) => reg && reg.entitlement_type === 'PAYMENT_REQUIRED' && reg.payment_status !== 'COMPLETED'

  const isGuestPending = getIsGuestPending(result)

  const handleCheckIn = async (reg = result) => {
    if (!reg) return
    setLoading(true)
    setError('')
    try {
      const options = collectPayment ? {
        record_payment: true,
        payment_method: paymentMethod,
        transaction_reference: txRef || `SMA-DOOR-${Date.now().toString(36).toUpperCase()}`,
      } : {}

      const res = await masterclassService.adminCheckIn(reg.registration_number, options)
      setResult(r => ({
        ...r,
        checked_in: true,
        payment_status: 'COMPLETED',
      }))
      setCheckedIn(true)
    } catch (err) {
      setError(err.message)
    } finally { setLoading(false) }
  }

  const verifyFromNumber = async (rawNumber) => {
    const number = extractRegNumber(rawNumber).toUpperCase()
    if (!number) return
    setLoading(true); setError(''); setResult(null); setCheckedIn(false); setCollectPayment(false); setTxRef('')
    try {
      const data = await masterclassService.verifyRegistration(number)
      const reg = data.registration
      setRegNum(reg.registration_number || number)
      setResult(reg)
      // Staff now see the entitlement and payment status clearly and decide the next step.
    } catch (err) {
      setError(err.message)
    } finally { setLoading(false) }
  }

  const handleVerify = async (e) => {
    e.preventDefault()
    await verifyFromNumber(regNum)
  }

  return (
    <div className="max-w-lg mx-auto space-y-6">
      {todayCount && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-10">
          {[
            ['Total', todayCount.total, 'text-primary-600 dark:text-primary-400'],
            ['Checked In', todayCount.checked_in, 'text-green-600 dark:text-green-400'],
            ['Today', todayCount.today, 'text-blue-600 dark:text-blue-400']
          ].map(([l, v, c]) => (
            <div key={l} className="stat-item">
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">{l}</span>
              <p className={`text-3xl ${c} font-display font-semibold mt-1`}>{v}</p>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={handleVerify} className="space-y-4">
        <div>
          <label className="cinema-label block mb-2">
            Registration Number or Scan QR
          </label>
          <div className="flex gap-2">
            <input type="text" placeholder="SMA-MC-2026-001" value={regNum}
              onChange={e => setRegNum(e.target.value)} autoFocus
              className="flex-1 px-4 py-3.5 font-sans text-sm text-white placeholder-cinema-muted/60 bg-cinema-graphite border-b border-white/10 focus:outline-none font-mono"
              style={{ fontSize: 16 }} />
            <button type="submit" disabled={loading}
              className="cinema-btn-primary !px-6 !py-3.5 !min-h-0">
              {loading ? <LoadingSpinner size="sm" inline /> : <Search className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="flex items-center gap-3 bg-red-500/10 p-4 text-red-400 font-sans text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" /> {error}
        </div>
      )}

      {result && (
        <div className="space-y-5 border-b border-white/5 pb-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-display text-white text-xl font-bold">{result.first_name} {result.last_name}</h3>
              <p className="text-cinema-muted/70 font-sans text-sm mt-1">{result.email}</p>
              <p className="text-cinema-glow font-mono text-sm mt-2 font-bold">{result.registration_number}</p>
            </div>
            {result.checked_in ? (
              <span className="flex items-center gap-2 text-green-400 font-sans text-sm font-semibold">
                <CheckCircle2 className="w-4 h-4" /> Checked In
              </span>
            ) : (
              <span className="text-cinema-glow font-sans text-sm font-semibold">
                Pending
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm font-sans">
            <div><span className="text-cinema-muted/60 text-xs uppercase tracking-widest block">Session</span><span className="text-cinema-soft/70 mt-1 block">{result.topic}</span></div>
            <div><span className="text-cinema-muted/60 text-xs uppercase tracking-widest block">Date</span><span className="text-cinema-soft/70 mt-1 block">{result.event_date ? new Date(result.event_date).toLocaleDateString() : '—'}</span></div>
            <div><span className="text-cinema-muted/60 text-xs uppercase tracking-widest block">Entitlement</span>
              <span className="text-cinema-soft/70 mt-1 block font-medium">
                {result.entitlement_type === 'FREE' ? 'SMA Student (Complimentary)' : 'Non-Student Guest (UGX 150,000)'}
              </span>
            </div>
            <div><span className="text-cinema-muted/60 text-xs uppercase tracking-widest block">Payment Status</span>
              <span className="mt-1 block">
                {result.payment_status === 'COMPLETED' ? (
                  <span className="text-green-400 font-semibold">Paid (UGX 150,000)</span>
                ) : result.entitlement_type === 'FREE' ? (
                  <span className="text-green-400">Free / Not Required</span>
                ) : (
                  <span className="text-[#EE8D21] font-semibold">Pending UGX 150,000</span>
                )}
              </span>
            </div>
          </div>

          {/* Guest Payment Gate Warning & Door Collection Box */}
          {isGuestPending && !result.checked_in && (
            <div className="bg-[#EE8D21]/10 border border-[#EE8D21]/40 p-4 space-y-3">
              <div className="flex items-center gap-2 text-[#EE8D21] font-sans font-bold text-xs uppercase tracking-wider">
                <AlertCircle className="w-4 h-4" /> Guest Fee Payment Required (UGX 150,000)
              </div>
              <p className="text-xs text-gray-300 font-sans">
                This attendee registered as a non-SMA guest and has not completed online payment.
              </p>

              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={collectPayment}
                  onChange={(e) => setCollectPayment(e.target.checked)}
                  className="rounded-none text-[#EE8D21] focus:ring-[#EE8D21]"
                />
                <span className="text-xs text-white font-sans font-medium">
                  Collect UGX 150,000 at reception now and confirm check-in
                </span>
              </label>

              {collectPayment && (
                <div className="space-y-3 pt-2 border-t border-white/10">
                  <div className="grid grid-cols-3 gap-2">
                    {['cash', 'mobile_money', 'bank_transfer'].map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setPaymentMethod(m)}
                        className={`p-2 text-center text-xs font-sans uppercase tracking-wider transition-all ${
                          paymentMethod === m
                            ? 'bg-[#EE8D21] text-black font-bold'
                            : 'bg-white/5 text-gray-400 hover:text-white border border-white/10'
                        }`}
                      >
                        {m.replace('_', ' ')}
                      </button>
                    ))}
                  </div>

                  <input
                    type="text"
                    value={txRef}
                    onChange={(e) => setTxRef(e.target.value)}
                    placeholder="Transaction Reference / Receipt #"
                    className="w-full bg-cinema-graphite border-b border-white/10 px-3 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#EE8D21]"
                  />
                </div>
              )}
            </div>
          )}

          {!result.checked_in && !checkedIn && (
            <button
              onClick={handleCheckIn}
              disabled={loading || (isGuestPending && !collectPayment)}
              className={`w-full justify-center text-sm tracking-widest uppercase py-3.5 px-4 font-bold flex items-center gap-2 transition-all ${
                isGuestPending && !collectPayment
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'cinema-btn-primary'
              }`}
            >
              {loading ? (
                <LoadingSpinner size="sm" inline />
              ) : isGuestPending ? (
                collectPayment ? (
                  <><DollarSign className="w-5 h-5" /> Collect UGX 150,000 & Check In</>
                ) : (
                  <><AlertCircle className="w-5 h-5" /> Payment Required Before Check-In</>
                )
              ) : (
                <><CheckCircle2 className="w-5 h-5" /> Mark as Checked In</>
              )}
            </button>
          )}

          {checkedIn && (
            <div className="text-center py-4">
              <div className="text-4xl mb-2">✅</div>
              <p className="text-green-400 font-display text-lg font-bold">Successfully Checked In!</p>
            </div>
          )}

          <button onClick={() => { setResult(null); setRegNum(''); setCheckedIn(false); setError(''); setCollectPayment(false) }}
            className="w-full py-3 text-cinema-muted/70 hover:text-white font-sans text-sm transition-colors">
            Verify Another Pass
          </button>
        </div>
      )}
    </div>
  )
}

// ── Analytics ──────────────────────────────────────────────────────────────
function AnalyticsTab() {
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (startDate && endDate) { params.start_date = startDate; params.end_date = endDate }
      const data = await masterclassService.adminGetAnalytics(params)
      setAnalytics(data)
    } catch (err) { console.error(err) } finally { setLoading(false) }
  }, [startDate, endDate])

  useEffect(() => { load() }, [load])

  if (loading) return <LoadingSpinner className="py-16" />

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
        <div className="w-full sm:w-auto">
          <label className="block text-xs uppercase tracking-widest text-cinema-muted/70 mb-2 font-sans">Start Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full px-4 py-3 font-sans text-sm text-white bg-cinema-graphite border-b border-white/10" style={{ colorScheme: 'dark' }} />
        </div>
        <div className="w-full sm:w-auto">
          <label className="block text-xs uppercase tracking-widest text-cinema-muted/70 mb-2 font-sans">End Date</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full px-4 py-3 font-sans text-sm text-white bg-cinema-graphite border-b border-white/10" style={{ colorScheme: 'dark' }} />
        </div>
        <button onClick={load} className="cinema-btn-primary !px-4 !py-3 !min-h-0 text-sm w-full sm:w-auto">Apply</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
        {[
          { label: 'Registrations', value: analytics?.registrations || 0, color: 'text-primary-600 dark:text-primary-400' },
          { label: 'Attendees', value: analytics?.attendance || 0, color: 'text-green-600 dark:text-green-400' },
          { label: 'Certificates', value: analytics?.certificates || 0, color: 'text-blue-600 dark:text-blue-400' },
          { label: 'Badges', value: analytics?.badges || 0, color: 'text-purple-600 dark:text-purple-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="stat-item">
            <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">{label}</span>
            <p className={`text-3xl ${color} font-display font-semibold mt-1`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
          <div className="section-header mb-4">
            <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">Recent Events</h3>
          </div>
          <div className="space-y-3">
            {(analytics?.recentEvents || []).map(e => (
              <div key={e.id} className="data-row flex items-center justify-between py-3">
                <div className="flex-1">
                  <p className="text-gray-900 dark:text-white text-sm font-medium">{e.topic}</p>
                  <p className="text-gray-500 dark:text-gray-500 text-xs">{new Date(e.event_date).toLocaleDateString()}</p>
                </div>
                <div className="text-right text-xs">
                  <p className="text-gray-500 dark:text-gray-400">{e.total_reg} registered</p>
                  <p className="text-green-600 dark:text-green-400">{e.attended} attended</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
          <div className="section-header mb-4">
            <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">Top Events by Registrations</h3>
          </div>
          <div className="space-y-3">
            {(analytics?.popularEvents || []).map((e, i) => (
              <div key={e.id} className="data-row flex items-center justify-between py-3">
                <div className="flex items-center gap-3 flex-1">
                  <span className="text-xs font-bold text-gray-900 dark:text-white w-6">{i + 1}</span>
                  <p className="text-gray-900 dark:text-white text-sm font-medium">{e.topic}</p>
                </div>
                <span className="text-primary-600 dark:text-primary-400 font-bold">{e.registrations}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Attendance Report ─────────────────────────────────────────────────────
function AttendanceReportTab() {
  const [masterclasses, setMasterclasses] = useState([])
  const [selected, setSelected] = useState('')
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    masterclassService.adminGetMasterclasses().then(d => setMasterclasses(d.masterclasses || [])).catch(console.error)
  }, [])

  const load = async (id) => {
    setSelected(id)
    setLoading(true)
    try {
      const data = await masterclassService.adminGetAttendanceReport(id)
      setReport(data)
    } catch (err) { console.error(err) } finally { setLoading(false) }
  }

  return (
    <div className="space-y-6">
      <div className="relative max-w-sm">
        <select value={selected} onChange={e => load(e.target.value)} className="w-full appearance-none px-4 py-3 font-sans text-sm text-white bg-cinema-graphite border-b border-white/10 focus:outline-none focus:border-cinema-glow" style={{ colorScheme: 'dark' }}>
          <option value="">Select a masterclass</option>
          {masterclasses.map(m => <option key={m.id} value={m.id}>{m.topic}</option>)}
        </select>
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted/60 pointer-events-none" />
      </div>

      {report?.summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-10">
          {[
            ['Total', report.summary.total, 'text-primary-600 dark:text-primary-400'],
            ['Attended', report.summary.attended, 'text-green-600 dark:text-green-400'],
            ['Rate', `${report.summary.attendance_rate || 0}%`, 'text-blue-600 dark:text-blue-400'],
          ].map(([l, v, c]) => (
            <div key={l} className="stat-item">
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">{l}</span>
              <p className={`text-3xl ${c} font-display font-semibold mt-1`}>{v}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? <LoadingSpinner className="py-16" /> : report?.registrations && (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  {['Name', 'Email', 'Phone', 'Checked In', 'Certificate', 'Confirmed At'].map(h => (
                    <th key={h} className="text-left px-4 py-3.5 text-cinema-gold text-xs uppercase tracking-[0.14em] font-sans font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.registrations.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-cinema-muted/60 font-sans text-sm">No registrations</td></tr>
                ) : report.registrations.map(r => (
                  <tr key={r.id} className="table-row border-b border-white/5">
                    <td className="px-4 py-3.5 font-sans text-sm text-white whitespace-nowrap">{r.first_name} {r.last_name}</td>
                    <td className="px-4 py-3.5 font-sans text-xs text-cinema-muted">{r.email}</td>
                    <td className="px-4 py-3.5 font-sans text-xs text-cinema-muted">{r.phone || '—'}</td>
                    <td className="px-4 py-3.5">
                      {r.checked_in ? <span className="text-green-400 text-xs flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Yes</span> : <span className="text-cinema-muted/60 text-xs">No</span>}
                    </td>
                    <td className="px-4 py-3.5 font-sans text-xs text-cinema-glow font-mono">{r.certificate_number || '—'}</td>
                    <td className="px-4 py-3.5 font-sans text-xs text-cinema-muted/60 whitespace-nowrap">{r.attendance_confirmed_at ? new Date(r.attendance_confirmed_at).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {report.registrations.length === 0 ? (
              <div className="border border-white/10 p-6 text-center text-cinema-muted/60 font-sans text-sm">No registrations</div>
            ) : report.registrations.map(r => (
              <div key={r.id} className="border border-white/10 bg-cinema-graphite/20 p-3.5 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-sans text-sm text-white font-medium truncate">{r.first_name} {r.last_name}</p>
                  {r.checked_in ? <span className="text-green-400 text-xs flex items-center gap-1 shrink-0"><CheckCircle2 className="w-3.5 h-3.5" /> Yes</span> : <span className="text-cinema-muted/60 text-xs shrink-0">No</span>}
                </div>
                <div className="text-xs font-sans space-y-1">
                  <p className="text-cinema-muted truncate">{r.email}</p>
                  <p className="text-cinema-muted">{r.phone || '—'}</p>
                  <p><span className="text-cinema-muted/60">Certificate:</span> <span className="text-cinema-glow font-mono">{r.certificate_number || '—'}</span></p>
                  <p><span className="text-cinema-muted/60">Confirmed:</span> <span className="text-cinema-muted/60">{r.attendance_confirmed_at ? new Date(r.attendance_confirmed_at).toLocaleString() : '—'}</span></p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── Reminders ─────────────────────────────────────────────────────────────
function RemindersTab() {
  const [window, setWindow] = useState('3days')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  const send = async () => {
    setLoading(true)
    setResult(null)
    try {
      const data = await masterclassService.adminSendReminders(window)
      setResult(data)
    } catch (err) { console.error(err) } finally { setLoading(false) }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h3 className="font-display text-white text-lg font-bold mb-4">Send Reminder Emails</h3>
        <p className="text-cinema-muted font-sans text-sm mb-4">
          Send reminder emails to registered attendees for upcoming masterclasses. Avoid duplicate sends by window.
        </p>
        <div className="flex flex-wrap gap-3 mb-6">
          {[
            { id: '3days', label: '3 Days Before' },
            { id: '1day', label: '1 Day Before' },
            { id: '2hours', label: '2 Hours Before' },
          ].map(({ id, label }) => (
            <button
              key={id}
              onClick={() => setWindow(id)}
              className={`${
                window === id
                  ? 'cinema-btn-primary !px-4 !py-2.5 !min-h-0 text-sm'
                  : 'cinema-btn-ghost !px-4 !py-2.5 !min-h-0 text-sm'
              }`}
            >
              <Clock className="w-4 h-4 inline-block mr-2" />{label}
            </button>
          ))}
        </div>
        <button onClick={send} disabled={loading}
          className="cinema-btn-primary !px-6 !py-3.5 !min-h-0 text-sm uppercase disabled:opacity-50">
          {loading ? <LoadingSpinner size="sm" inline /> : <Mail className="w-4 h-4" />}
          Send Reminders
        </button>
        {result && (
          <div className="mt-4 text-green-400 font-sans text-sm">
            Sent {result.sent} reminder(s) across {result.events} event(s).
          </div>
        )}
      </div>
    </div>
  )
}



// ── Attendance Certificates ────────────────────────────────────────────────
function AttendanceCertificatesTab() {
  const [certs, setCerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [monthKey, setMonthKey] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })

  useEffect(() => {
    fetchCerts()
  }, [monthKey, search])

  const fetchCerts = async () => {
    try {
      setLoading(true)
      const params = { month_key: monthKey }
      if (search.trim()) params.search = search.trim()
      const data = await masterclassService.adminGetAttendanceCertificates(params)
      setCerts(data.certificates || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const formatMonthKey = (k) => {
    const [y, m] = k.split('-')
    return new Date(`${y}-${m}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="font-display text-lg sm:text-xl text-white font-bold">Monthly Attendance Certificates</h2>
          <p className="text-cinema-muted font-sans text-xs sm:text-sm mt-1">Awarded when an attendee completes the monthly masterclass on the last Friday of the month.</p>
        </div>
        <div className="text-cinema-glow font-display text-2xl font-bold shrink-0">{certs.length}</div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="month"
          value={monthKey}
          onChange={e => setMonthKey(e.target.value)}
          className="bg-cinema-graphite border-b border-white/10 text-white px-3 py-2 font-sans text-sm focus:outline-none focus:border-cinema-glow"
        />
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cinema-muted w-4 h-4" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-cinema-graphite border-b border-white/10 text-white pl-9 pr-3 py-2 font-sans text-sm focus:outline-none focus:border-cinema-glow"
          />
        </div>
      </div>

      {loading ? <LoadingSpinner className="py-16" /> : certs.length === 0 ? (
        <div className="text-center py-12">
          <Award className="w-10 h-10 text-cinema-muted mx-auto mb-3" />
          <p className="text-white font-sans">No certificates for {formatMonthKey(monthKey)}</p>
          <p className="text-cinema-muted text-sm mt-1">Try another month or check the attendance report.</p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Student</th>
                  <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Month</th>
                  <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Certificate Number</th>
                  <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Sessions</th>
                  <th className="text-right px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {certs.map(cert => (
                  <tr key={cert.id} className="table-row border-b border-white/5">
                    <td className="px-4 py-3">
                      <div className="font-sans text-sm text-white">{cert.student_name}</div>
                      <div className="font-sans text-xs text-cinema-muted">{cert.student_email}</div>
                    </td>
                    <td className="px-4 py-3 font-sans text-sm text-white">{cert.month_label}</td>
                    <td className="px-4 py-3 font-mono text-xs text-cinema-glow">{cert.certificate_number}</td>
                    <td className="px-4 py-3 font-sans text-sm text-white">{cert.session_count}</td>
                    <td className="px-4 py-3 text-right">
                      <a
                        href={`${import.meta.env.VITE_API_URL || '/api'}/masterclass/attendance-certificate/${cert.certificate_number}/view`}
                        target="_blank" rel="noreferrer"
                        className="text-cinema-electric hover:text-cinema-gold transition-colors inline-flex items-center gap-1 mr-3"
                        title="View"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <a
                        href={`${import.meta.env.VITE_API_URL || '/api'}/masterclass/attendance-certificate/${cert.certificate_number}/download`}
                        target="_blank" rel="noreferrer"
                        className="text-cinema-gold hover:text-cinema-glow transition-colors inline-flex items-center gap-1"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {certs.map(cert => (
              <div key={cert.id} className="border border-white/10 bg-cinema-graphite/20 p-3.5 space-y-2">
                <div>
                  <p className="font-sans text-sm text-white font-medium">{cert.student_name}</p>
                  <p className="font-sans text-xs text-cinema-muted truncate">{cert.student_email}</p>
                </div>
                <div className="flex items-center justify-between gap-2 text-xs font-sans">
                  <span className="text-cinema-muted">{cert.month_label}</span>
                  <span className="text-cinema-muted/60">{cert.session_count} sessions</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-cinema-glow truncate">{cert.certificate_number}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <a href={`${import.meta.env.VITE_API_URL || '/api'}/masterclass/attendance-certificate/${cert.certificate_number}/view`} target="_blank" rel="noreferrer" className="text-cinema-electric hover:text-cinema-gold" title="View"><ExternalLink className="w-4 h-4" /></a>
                    <a href={`${import.meta.env.VITE_API_URL || '/api'}/masterclass/attendance-certificate/${cert.certificate_number}/download`} target="_blank" rel="noreferrer" className="text-cinema-gold hover:text-cinema-glow" title="Download"><Download className="w-4 h-4" /></a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── Single-Session Certificates Tab (T14.8F) ────────────────────────────────
function SingleSessionCertificatesTab({ initialSearch, onSearchConsumed }) {
  const [certs, setCerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterMC, setFilterMC] = useState('all')
  const [sessions, setSessions] = useState([])
  const [regenerating, setRegenerating] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [previewCertNumber, setPreviewCertNumber] = useState(null)

  useEffect(() => {
    fetchSessions()
  }, [])

  // Consume pre-populated search from another tab (e.g. RegistrationsTab "View Certificates")
  useEffect(() => {
    if (initialSearch) {
      setSearch(initialSearch)
      if (onSearchConsumed) onSearchConsumed()
    }
  }, [initialSearch])

  useEffect(() => {
    fetchCerts()
  }, [search, filterMC])

  const fetchSessions = async () => {
    try {
      const data = await masterclassService.adminGetMasterclasses()
      setSessions(data.masterclasses || [])
    } catch (err) {
      console.error(err)
    }
  }

  const fetchCerts = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filterMC !== 'all') params.masterclass_id = filterMC
      if (search.trim()) params.search = search.trim()
      const data = await masterclassService.adminGetCertificates(params)
      setCerts(data.certificates || [])
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleRegenerate = async (certNumber) => {
    if (!window.confirm(`Regenerate PDF for certificate ${certNumber}? This will refresh the PDF using the current template. The certificate record, number, and all attendance data remain unchanged.`)) return
    setRegenerating(certNumber)
    try {
      await masterclassService.adminRegenerateCertificate(certNumber)
      toast.success('Certificate PDF regenerated')
    } catch (err) {
      console.error(err)
      toast.error('Failed to regenerate certificate')
    } finally {
      setRegenerating(null)
    }
  }

  const handleView = async (certNumber) => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${apiBase}/masterclass/admin/certificate/${certNumber}/view`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to fetch certificate')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      setPreviewUrl(url)
      setPreviewCertNumber(certNumber)
    } catch (err) {
      console.error(err)
      toast.error('Failed to view certificate')
    }
  }

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
    setPreviewCertNumber(null)
  }

  const handleDownload = async (certNumber) => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${apiBase}/masterclass/admin/certificate/${certNumber}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to download certificate')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `SMA-Certificate-${certNumber}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error(err)
      toast.error('Failed to download certificate')
    }
  }

  const apiBase = import.meta.env.VITE_API_URL || '/api'

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-display text-xl text-white font-bold">Single-Session Certificates</h2>
          <p className="text-cinema-muted font-sans text-sm mt-1">Issued automatically when an attendee checks in to a Masterclass session.</p>
        </div>
        <div className="text-cinema-glow font-display text-2xl font-bold">{certs.length}</div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative sm:w-auto">
          <select value={filterMC} onChange={e => setFilterMC(e.target.value)}
            className="w-full sm:w-auto appearance-none pl-4 pr-10 py-2 font-sans text-sm text-white bg-cinema-graphite border-b border-white/10 focus:outline-none focus:border-cinema-glow"
            style={{ colorScheme: 'dark' }}>
            <option value="all">All Sessions</option>
            {sessions.map(m => (
              <option key={m.id} value={m.id}>{m.topic.substring(0, 35)}…</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted/60 pointer-events-none" />
        </div>
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cinema-muted w-4 h-4" />
          <input
            type="text"
            placeholder="Search by name, email, or certificate number..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full bg-cinema-graphite border-b border-white/10 text-white pl-9 pr-9 py-2 font-sans text-sm focus:outline-none focus:border-cinema-glow"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-cinema-muted hover:text-white p-1 transition-colors"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {loading ? <LoadingSpinner className="py-16" /> : certs.length === 0 ? (
        <div className="text-center py-12">
          <Award className="w-10 h-10 text-cinema-muted mx-auto mb-3" />
          <p className="text-white font-sans">No certificates found</p>
          <p className="text-cinema-muted text-sm mt-1">Certificates are issued automatically when attendees check in.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Attendee</th>
                <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Masterclass</th>
                <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Date</th>
                <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Certificate No.</th>
                <th className="text-right px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-sans font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {certs.map(cert => (
                <tr key={cert.id} className="table-row border-b border-white/5">
                  <td className="px-4 py-3">
                    <div className="font-sans text-sm text-white">{cert.student_name}</div>
                    <div className="font-sans text-xs text-cinema-muted">{cert.student_email}</div>
                  </td>
                  <td className="px-4 py-3 font-sans text-sm text-white">{cert.masterclass_topic || cert.topic || '—'}</td>
                  <td className="px-4 py-3 font-sans text-xs text-cinema-muted whitespace-nowrap">
                    {cert.event_date ? new Date(cert.event_date).toLocaleDateString('en-UG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-cinema-glow">{cert.certificate_number}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => setSearch(cert.student_email)}
                      className="text-cinema-glow hover:text-cinema-gold transition-colors inline-flex items-center gap-1 mr-3"
                      title={`Show all certificates for ${cert.student_name}`}
                    >
                      <Award className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleView(cert.certificate_number)}
                      className="text-cinema-electric hover:text-cinema-gold transition-colors inline-flex items-center gap-1 mr-3"
                      title="View"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDownload(cert.certificate_number)}
                      className="text-cinema-gold hover:text-cinema-glow transition-colors inline-flex items-center gap-1 mr-3"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRegenerate(cert.certificate_number)}
                      disabled={regenerating === cert.certificate_number}
                      className="text-cinema-muted hover:text-cinema-glow transition-colors inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-wait"
                      title="Regenerate PDF"
                    >
                      <RefreshCw className={`w-4 h-4 ${regenerating === cert.certificate_number ? 'animate-spin' : ''}`} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* In-page certificate preview modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={closePreview}>
          <div className="bg-cinema-graphite border border-white/10 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h3 className="font-display text-white font-bold text-sm">Certificate Preview</h3>
                <p className="font-mono text-xs text-cinema-glow mt-0.5">{previewCertNumber}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(previewCertNumber)}
                  className="cinema-btn-primary !px-3 !py-1.5 !min-h-0 text-xs inline-flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" /> Download
                </button>
                <button
                  onClick={closePreview}
                  className="p-1.5 text-cinema-muted hover:text-white bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                  title="Close preview"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-black/40">
              <iframe src={previewUrl} title="Certificate PDF" className="w-full h-[70vh] border-0" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
function PaymentVerificationsTab() {
  const [verifications, setVerifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [selectedProof, setSelectedProof] = useState(null)
  const [proofUrl, setProofUrl] = useState(null)
  const [proofLoading, setProofLoading] = useState(false)
  const [rejectingItem, setRejectingItem] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [actionSuccess, setActionSuccess] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await masterclassService.adminGetPendingVerifications(
        filterStatus ? { status: filterStatus } : {}
      )
      setVerifications(data.verifications || [])
    } catch (err) {
      setError(err.message || 'Failed to load verifications')
    } finally {
      setLoading(false)
    }
  }, [filterStatus])

  // T14.2: Fetch a short-lived presigned URL for viewing a masterclass payment proof
  const handleViewProof = async (item) => {
    setSelectedProof(item)
    setProofUrl(null)
    setProofLoading(true)
    try {
      const data = await masterclassService.adminGetProofUrl(item.id)
      if (data?.url) {
        setProofUrl(data.url)
      } else {
        setError('No proof file available for this registration.')
      }
    } catch (err) {
      setError(err.message || 'Failed to retrieve proof file.')
    } finally {
      setProofLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [load])

  const handleVerify = async (item) => {
    if (!window.confirm(`Verify and approve payment of UGX 150,000 for ${item.first_name} ${item.last_name} (${item.registration_number})?\n\nThis will atomically generate an authoritative paid invoice and payment record in SMA Finance.`)) {
      return
    }
    setActionLoading(true)
    setActionSuccess('')
    try {
      const res = await masterclassService.adminVerifyPayment({
        registration_id: item.id,
        registration_number: item.registration_number,
        transaction_reference: item.payment_reference,
        payment_method: item.payment_method || 'mobile_money',
        notes: `Verified in Admin Portal by Staff for ${item.topic}`,
      })
      if (res.success) {
        setActionSuccess(`✓ Payment verified successfully for ${item.registration_number}. Admission access confirmed!`)
        load()
      }
    } catch (err) {
      alert(`Verification failed: ${err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  const handleRejectSubmit = async (e) => {
    e.preventDefault()
    if (!rejectReason.trim()) {
      alert('Please specify a rejection reason so the attendee knows what to correct.')
      return
    }
    setActionLoading(true)
    try {
      const res = await masterclassService.adminRejectPayment({
        registration_id: rejectingItem.id,
        registration_number: rejectingItem.registration_number,
        reason: rejectReason.trim(),
      })
      if (res.success) {
        setActionSuccess(`Payment proof rejected for ${rejectingItem.registration_number}. Attendee will be notified.`)
        setRejectingItem(null)
        setRejectReason('')
        load()
      }
    } catch (err) {
      alert(`Rejection failed: ${err.message}`)
    } finally {
      setActionLoading(false)
    }
  }

  const pendingCount = verifications.filter(v => v.payment_status === 'PENDING_VERIFICATION').length
  const rejectedCount = verifications.filter(v => v.payment_status === 'REJECTED').length

  return (
    <div className="space-y-6">
      {/* Header / Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-10">
        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Awaiting Verification</span>
          <p className="text-3xl text-sky-600 dark:text-sky-400 font-display font-semibold mt-1">{pendingCount}</p>
        </div>
        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Rejected Proofs</span>
          <p className="text-3xl text-red-600 dark:text-red-400 font-display font-semibold mt-1">{rejectedCount}</p>
        </div>
        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Total Review Queue</span>
          <p className="text-3xl text-gray-900 dark:text-white font-display font-semibold mt-1">{verifications.length}</p>
        </div>
      </div>

      {actionSuccess && (
        <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 text-green-400 text-xs font-sans">
          <CheckCircle2 className="w-4 h-4 shrink-0" /> {actionSuccess}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-sans">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2.5 font-sans text-sm text-white bg-cinema-graphite border-b border-white/10 focus:outline-none"
            style={{ colorScheme: 'dark' }}
          >
            <option value="">All Review States (Pending & Rejected)</option>
            <option value="PENDING_VERIFICATION">Pending Verification Only</option>
            <option value="REJECTED">Rejected Proofs Only</option>
          </select>

          <button onClick={load} className="cinema-btn-ghost !p-2.5" title="Refresh list">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <p className="text-xs text-cinema-muted font-sans">
          Official MM: <strong className="text-cinema-glow font-mono">0763875564</strong> (Kiggundu Yacob)
        </p>
      </div>

      {/* Verifications Table */}
      {loading ? (
        <LoadingSpinner className="py-16" />
      ) : verifications.length === 0 ? (
        <div className="text-center py-16 border border-white/5 bg-white/[0.02]">
          <ShieldCheck className="w-12 h-12 text-cinema-muted/40 mx-auto mb-3" />
          <h4 className="text-sm font-bold text-white font-sans uppercase tracking-wider">No Pending Verifications</h4>
          <p className="text-xs text-cinema-muted font-sans mt-1">
            All submitted guest payment proofs have been reviewed and verified.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                {['Reg #', 'Registrant', 'Session Topic', 'Method / Ref', 'Proof File', 'Submitted At', 'Status', 'Actions'].map(h => (
                  <th key={h} className="text-left px-4 py-3.5 text-cinema-gold text-xs uppercase tracking-[0.14em] font-sans font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {verifications.map((item) => (
                <tr key={item.id} className="table-row border-b border-white/5">
                  <td className="px-4 py-3.5 font-mono text-xs text-cinema-glow font-bold">
                    {item.registration_number}
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="font-sans text-sm font-medium text-white">{item.first_name} {item.last_name}</div>
                    <div className="font-sans text-xs text-cinema-muted">{item.phone || 'No phone'} · {item.email}</div>
                  </td>
                  <td className="px-4 py-3.5 font-sans text-xs text-gray-300 max-w-[200px] truncate" title={item.topic}>
                    {item.topic}
                  </td>
                  <td className="px-4 py-3.5 font-sans text-xs">
                    <span className="uppercase text-gray-400 text-xs block font-medium">{item.payment_method || 'Mobile Money'}</span>
                    <span className="font-mono text-white text-xs font-semibold">{item.payment_reference || '—'}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    {item.has_proof ? (
                      <button
                        onClick={() => handleViewProof(item)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/10 hover:bg-white/20 text-white font-sans text-xs transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5 text-cinema-gold" /> View Proof
                      </button>
                    ) : (
                      <span className="text-gray-500 font-sans text-xs italic">Ref Only</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 font-sans text-xs text-cinema-muted whitespace-nowrap">
                    {item.proof_submitted_at ? new Date(item.proof_submitted_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3.5">
                    {item.payment_status === 'PENDING_VERIFICATION' ? (
                      <span className="inline-flex items-center gap-1 text-sky-400 bg-sky-500/10 border border-sky-500/30 px-2 py-0.5 font-sans text-xs font-semibold">
                        <Clock className="w-3 h-3" /> Pending Review
                      </span>
                    ) : item.payment_status === 'REJECTED' ? (
                      <span className="inline-flex items-center gap-1 text-red-400 bg-red-500/10 border border-red-500/30 px-2 py-0.5 font-sans text-xs font-semibold" title={item.rejection_reason || ''}>
                        <XCircle className="w-3 h-3" /> Rejected
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs font-sans">{item.payment_status}</span>
                    )}
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleVerify(item)}
                        disabled={actionLoading}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white font-sans text-xs font-bold uppercase tracking-wider transition-colors inline-flex items-center gap-1"
                        title="Verify Payment & Create Authoritative Invoice + Payment"
                      >
                        <Check className="w-3.5 h-3.5" /> Approve (150k)
                      </button>
                      <button
                        onClick={() => { setRejectingItem(item); setRejectReason('') }}
                        disabled={actionLoading}
                        className="px-2.5 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/40 font-sans text-xs uppercase tracking-wider transition-colors"
                        title="Reject Proof Screenshot"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Proof Preview Modal */}
      {selectedProof && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-cinema-black border border-white/10 w-full max-w-2xl p-6 space-y-4 text-white shadow-2xl relative max-h-[90vh] flex flex-col">
            <button
              onClick={() => { setSelectedProof(null); setProofUrl(null) }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <XCircle className="w-6 h-6" />
            </button>

            <div>
              <span className="text-xs uppercase tracking-widest text-[#EE8D21] font-sans font-bold">
                Payment Proof Inspection
              </span>
              <h3 className="text-lg font-bold text-white font-display">
                {selectedProof.first_name} {selectedProof.last_name} · {selectedProof.registration_number}
              </h3>
              <p className="text-xs text-cinema-muted font-sans mt-1">
                Method: <strong className="text-white uppercase">{selectedProof.payment_method}</strong> · Reference: <span className="font-mono text-cinema-glow font-bold">{selectedProof.payment_reference || 'N/A'}</span>
              </p>
            </div>

            <div className="flex-1 overflow-auto bg-black/60 p-2 border border-white/10 flex items-center justify-center min-h-[300px]">
              {proofLoading ? (
                <div className="text-center p-8">
                  <LoadingSpinner />
                  <p className="text-sm text-gray-300 font-sans mt-3">Loading proof from secure storage...</p>
                </div>
              ) : proofUrl ? (
                proofUrl.toLowerCase().includes('.pdf') ? (
                  <div className="text-center p-8 space-y-3">
                    <FileText className="w-16 h-16 text-cinema-gold mx-auto" />
                    <p className="text-sm text-gray-300 font-sans">PDF Payment Document Attached</p>
                    <a
                      href={proofUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="cinema-btn-primary inline-flex items-center gap-2 text-xs"
                    >
                      <ExternalLink className="w-4 h-4" /> Open PDF in New Tab
                    </a>
                  </div>
                ) : (
                  <img
                    src={proofUrl}
                    alt="Payment Proof"
                    className="max-h-[60vh] max-w-full object-contain mx-auto"
                  />
                )
              ) : (
                <div className="text-center p-8">
                  <p className="text-sm text-gray-400 font-sans">No proof file available or failed to load.</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              {proofUrl && (
                <a
                  href={proofUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="cinema-btn-ghost text-xs inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Full Resolution
                </a>
              )}
              <button
                onClick={() => { const item = selectedProof; setSelectedProof(null); setProofUrl(null); handleVerify(item) }}
                className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white font-sans text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1"
              >
                <Check className="w-4 h-4" /> Approve & Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      {rejectingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="bg-cinema-black border border-white/10 w-full max-w-md p-6 space-y-4 text-white shadow-2xl relative">
            <button
              onClick={() => setRejectingItem(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <XCircle className="w-5 h-5" />
            </button>

            <div>
              <span className="text-xs uppercase tracking-widest text-red-400 font-sans font-bold">
                Reject Payment Proof
              </span>
              <h3 className="text-lg font-bold text-white font-display">
                {rejectingItem.first_name} {rejectingItem.last_name} ({rejectingItem.registration_number})
              </h3>
              <p className="text-xs text-cinema-muted font-sans mt-1">
                Please enter a clear reason for rejecting this proof. The attendee will see this message and be prompted to re-upload.
              </p>
            </div>

            <form onSubmit={handleRejectSubmit} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gray-400 font-sans mb-1 font-medium">
                  Rejection Reason *
                </label>
                <textarea
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="e.g. Screenshot unreadable, amount sent was incorrect, or transaction ID not found in MoMo statement."
                  required
                  className="w-full bg-black/40 border border-white/20 p-3 text-sm text-white focus:outline-none focus:border-red-400"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setRejectingItem(null)}
                  className="flex-1 cinema-btn-ghost text-xs justify-center"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-500 text-white font-sans text-xs font-bold uppercase tracking-wider transition-colors"
                >
                  {actionLoading ? <LoadingSpinner size="sm" inline /> : 'Confirm Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Funds Ledger Tab ─────────────────────────────────────────────────────────
function FundsTab() {
  const { user } = useAuthStore()
  const canManage = user?.user_type === 'admin' || user?.user_type === 'finance'

  const [transactions, setTransactions] = useState([])
  const [stats, setStats] = useState(null)
  const [masterclasses, setMasterclasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [filterType, setFilterType] = useState('')
  const [filterMasterclass, setFilterMasterclass] = useState('')

  const [formData, setFormData] = useState({
    masterclass_id: '',
    transaction_type: 'REGISTRATION_FEE',
    direction: '',
    amount: '',
    currency: 'UGX',
    description: '',
    payment_method: 'cash',
    transaction_reference: '',
    payer_payee_name: '',
    payer_payee_contact: '',
    transaction_date: new Date().toISOString().split('T')[0],
  })

  const TRANSACTION_TYPES = [
    { value: 'REGISTRATION_FEE', label: 'Registration Fee', direction: 'INCOME' },
    { value: 'DONATION', label: 'Donation', direction: 'INCOME' },
    { value: 'SPONSORSHIP', label: 'Sponsorship', direction: 'INCOME' },
    { value: 'SPEAKER_HONORARIUM', label: 'Speaker Honorarium', direction: 'EXPENSE' },
    { value: 'EVENT_EXPENSE', label: 'Event Expense', direction: 'EXPENSE' },
  ]

  const PAYMENT_METHODS = [
    { value: 'cash', label: 'Cash' },
    { value: 'mobile_money', label: 'Mobile Money' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'card', label: 'Card' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'other', label: 'Other' },
  ]

  const fetchFunds = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (filterType) params.transaction_type = filterType
      if (filterMasterclass) params.masterclass_id = filterMasterclass
      const data = await masterclassService.adminGetFunds(params)
      setTransactions(data.transactions || [])
      setStats(data.stats || null)
    } catch (err) {
      toast.error(err.message || 'Failed to load funds')
    } finally {
      setLoading(false)
    }
  }, [filterType, filterMasterclass])

  const fetchMasterclasses = useCallback(async () => {
    try {
      const data = await masterclassService.adminGetMasterclasses()
      setMasterclasses(data.masterclasses || [])
    } catch {
      // non-critical
    }
  }, [])

  useEffect(() => { fetchFunds() }, [fetchFunds])
  useEffect(() => { fetchMasterclasses() }, [fetchMasterclasses])

  const resetForm = () => {
    setFormData({
      masterclass_id: '',
      transaction_type: 'REGISTRATION_FEE',
      direction: '',
      amount: '',
      currency: 'UGX',
      description: '',
      payment_method: 'cash',
      transaction_reference: '',
      payer_payee_name: '',
      payer_payee_contact: '',
      transaction_date: new Date().toISOString().split('T')[0],
    })
    setEditingId(null)
  }

  const handleOpenAdd = () => {
    resetForm()
    setShowModal(true)
  }

  const handleOpenEdit = (tx) => {
    setEditingId(tx.id)
    setFormData({
      masterclass_id: tx.masterclass_id || '',
      transaction_type: tx.transaction_type,
      direction: tx.direction || '',
      amount: String(tx.amount),
      currency: tx.currency || 'UGX',
      description: tx.description || '',
      payment_method: tx.payment_method || 'cash',
      transaction_reference: tx.transaction_reference || '',
      payer_payee_name: tx.payer_payee_name || '',
      payer_payee_contact: tx.payer_payee_contact || '',
      transaction_date: tx.transaction_date ? tx.transaction_date.split('T')[0] : new Date().toISOString().split('T')[0],
    })
    setShowModal(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Amount must be greater than 0')
      return
    }
    const payload = {
      ...formData,
      masterclass_id: formData.masterclass_id || null,
      amount: parseFloat(formData.amount),
    }
    try {
      if (editingId) {
        await masterclassService.adminUpdateFund(editingId, payload)
        toast.success('Transaction updated')
      } else {
        await masterclassService.adminCreateFund(payload)
        toast.success('Transaction recorded')
      }
      setShowModal(false)
      resetForm()
      fetchFunds()
    } catch (err) {
      toast.error(err.message || 'Failed to save transaction')
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fund transaction? This cannot be undone.')) return
    try {
      await masterclassService.adminDeleteFund(id)
      toast.success('Transaction deleted')
      fetchFunds()
    } catch (err) {
      toast.error(err.message || 'Failed to delete')
    }
  }

  const fmtMoney = (amt) => {
    const n = Number(amt) || 0
    return n.toLocaleString('en-UG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  }

  const typeLabel = (t) => TRANSACTION_TYPES.find(x => x.value === t)?.label || t
  const typeDirection = (t) => TRANSACTION_TYPES.find(x => x.value === t)?.direction || 'INCOME'
  const methodLabel = (m) => PAYMENT_METHODS.find(x => x.value === m)?.label || (m || '—')

  const netBalance = stats ? (Number(stats.total_income) - Number(stats.total_expenses)) : 0

  if (loading) return <LoadingSpinner />

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10">
        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Total Income</span>
          <p className="text-3xl text-green-600 dark:text-green-400 font-display font-semibold mt-1">{fmtMoney(stats?.total_income || 0)}</p>
        </div>
        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Total Expenses</span>
          <p className="text-3xl text-red-600 dark:text-red-400 font-display font-semibold mt-1">{fmtMoney(stats?.total_expenses || 0)}</p>
        </div>
        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Net Balance</span>
          <p className={`text-3xl font-display font-semibold mt-1 ${netBalance >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {fmtMoney(netBalance)}
          </p>
        </div>
        <div className="stat-item">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">Transactions</span>
          <p className="text-3xl text-gray-900 dark:text-white font-display font-semibold mt-1">{transactions.length}</p>
        </div>
      </div>

      {/* Breakdown by type */}
      {stats && (
        <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
          <div className="section-header mb-4">
            <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">Breakdown by Type</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-8">
            {[
              { key: 'registration_fee', label: 'Registration Fees', color: 'text-green-600 dark:text-green-400' },
              { key: 'donation', label: 'Donations', color: 'text-green-600 dark:text-green-400' },
              { key: 'sponsorship', label: 'Sponsorships', color: 'text-green-600 dark:text-green-400' },
              { key: 'speaker_honorarium', label: 'Speaker Honoraria', color: 'text-red-600 dark:text-red-400' },
              { key: 'event_expense', label: 'Event Expenses', color: 'text-red-600 dark:text-red-400' },
            ].map(item => (
              <div key={item.key} className="stat-item">
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-medium">{item.label}</span>
                <div className={`text-lg font-semibold ${item.color} mt-1`}>
                  {fmtMoney(stats[`${item.key}_total`] || 0)}
                </div>
                <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {stats[`${item.key}_count`] || 0} txn{stats[`${item.key}_count`] !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters + Add Button */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input rounded-none bg-cinema-graphite text-white text-sm py-2"
          >
            <option value="">All Types</option>
            {TRANSACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select
            value={filterMasterclass}
            onChange={(e) => setFilterMasterclass(e.target.value)}
            className="input rounded-none bg-cinema-graphite text-white text-sm py-2 max-w-[200px]"
          >
            <option value="">All Sessions</option>
            {masterclasses.map(m => <option key={m.id} value={m.id}>{m.topic?.substring(0, 40)}</option>)}
          </select>
          {(filterType || filterMasterclass) && (
            <button
              onClick={() => { setFilterType(''); setFilterMasterclass('') }}
              className="cinema-btn-ghost text-xs flex items-center gap-1"
            >
              <X size={14} /> Clear
            </button>
          )}
        </div>
        {canManage && (
          <button onClick={handleOpenAdd} className="cinema-btn-primary flex items-center gap-2 text-sm">
            <Plus size={16} /> Add Transaction
          </button>
        )}
      </div>

      {/* Transactions Table — Desktop */}
      <div className="hidden md:block overflow-x-auto border border-white/10">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 bg-white/5">
              <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-semibold">Date</th>
              <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-semibold">Type</th>
              <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-semibold">Description</th>
              <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-semibold">Session</th>
              <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-semibold">Payer / Payee</th>
              <th className="text-left px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-semibold">Method</th>
              <th className="text-right px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-semibold">Amount</th>
              {canManage && <th className="text-right px-4 py-3 text-cinema-gold text-xs uppercase tracking-widest font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {transactions.map(tx => (
              <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 text-sm text-cinema-muted/70 whitespace-nowrap">
                  {new Date(tx.transaction_date).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-sm whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold ${
                    tx.direction === 'INCOME' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
                  }`}>
                    {tx.direction === 'INCOME' ? <ArrowDownCircle size={12} /> : <ArrowUpCircle size={12} />}
                    {typeLabel(tx.transaction_type)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-white max-w-xs truncate" title={tx.description || ''}>
                  {tx.description || '—'}
                </td>
                <td className="px-4 py-3 text-sm text-cinema-muted/70 max-w-[180px] truncate" title={tx.masterclass_topic || ''}>
                  {tx.masterclass_topic || '—'}
                </td>
                <td className="px-4 py-3 text-sm text-cinema-muted/70">
                  {tx.payer_payee_name || '—'}
                </td>
                <td className="px-4 py-3 text-sm text-cinema-muted/70 whitespace-nowrap">
                  {methodLabel(tx.payment_method)}
                </td>
                <td className={`px-4 py-3 text-sm font-semibold text-right whitespace-nowrap ${
                  tx.direction === 'INCOME' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {tx.direction === 'INCOME' ? '+' : '−'}{fmtMoney(tx.amount)}
                  <span className="text-xs text-cinema-muted/50 ml-1">{tx.currency}</span>
                </td>
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => handleOpenEdit(tx)} className="text-blue-400 hover:text-white" title="Edit">
                        <Pencil size={16} />
                      </button>
                      <button onClick={() => handleDelete(tx.id)} className="text-red-400 hover:text-white" title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {transactions.length === 0 && (
          <div className="text-center py-12">
            <Wallet className="mx-auto text-cinema-muted/30 mb-3" size={40} />
            <p className="text-cinema-muted/70">No fund transactions recorded yet</p>
          </div>
        )}
      </div>

      {/* Transactions Cards — Mobile */}
      <div className="md:hidden space-y-0 cinema-list">
        {transactions.map(tx => (
          <div key={tx.id} className="cinema-list-row flex-col items-start gap-2 py-4">
            <div className="flex items-center justify-between w-full">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold ${
                tx.direction === 'INCOME' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
              }`}>
                {tx.direction === 'INCOME' ? <ArrowDownCircle size={12} /> : <ArrowUpCircle size={12} />}
                {typeLabel(tx.transaction_type)}
              </span>
              <span className={`text-sm font-semibold ${tx.direction === 'INCOME' ? 'text-green-400' : 'text-red-400'}`}>
                {tx.direction === 'INCOME' ? '+' : '−'}{fmtMoney(tx.amount)} {tx.currency}
              </span>
            </div>
            <div className="text-sm text-white">{tx.description || '—'}</div>
            <div className="text-xs text-cinema-muted/60 space-y-0.5 w-full">
              <div>{new Date(tx.transaction_date).toLocaleDateString()} · {methodLabel(tx.payment_method)}</div>
              {tx.payer_payee_name && <div>Payer/Payee: {tx.payer_payee_name}</div>}
              {tx.masterclass_topic && <div className="truncate">Session: {tx.masterclass_topic}</div>}
            </div>
            {canManage && (
              <div className="flex gap-2 pt-2 border-t border-white/10 w-full">
                <button onClick={() => handleOpenEdit(tx)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-blue-400 hover:text-blue-300">
                  <Pencil size={14} /> Edit
                </button>
                <button onClick={() => handleDelete(tx.id)} className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-red-400 hover:text-red-300">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        ))}
        {transactions.length === 0 && (
          <div className="text-center py-12">
            <Wallet className="mx-auto text-cinema-muted/30 mb-3" size={40} />
            <p className="text-cinema-muted/70">No fund transactions recorded yet</p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && canManage && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="cinema-glass-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-white text-lg font-bold">
                  {editingId ? 'Edit Transaction' : 'Add Fund Transaction'}
                </h3>
                <button onClick={() => { setShowModal(false); resetForm() }} className="text-cinema-muted hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Transaction Type */}
                <div>
                  <label className="block text-xs text-cinema-muted/60 uppercase tracking-wider mb-1.5">Transaction Type</label>
                  <select
                    value={formData.transaction_type}
                    onChange={(e) => setFormData({ ...formData, transaction_type: e.target.value, direction: typeDirection(e.target.value) })}
                    className="input w-full rounded-none bg-cinema-graphite text-white"
                  >
                    {TRANSACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label} ({t.direction})</option>)}
                  </select>
                </div>

                {/* Masterclass + Amount */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-cinema-muted/60 uppercase tracking-wider mb-1.5">Masterclass Session</label>
                    <select
                      value={formData.masterclass_id}
                      onChange={(e) => setFormData({ ...formData, masterclass_id: e.target.value })}
                      className="input w-full rounded-none bg-cinema-graphite text-white"
                    >
                      <option value="">— Unallocated —</option>
                      {masterclasses.map(m => <option key={m.id} value={m.id}>{m.topic?.substring(0, 50)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-cinema-muted/60 uppercase tracking-wider mb-1.5">Amount (UGX)</label>
                    <input
                      type="number"
                      step="any"
                      min="0"
                      required
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="input w-full rounded-none bg-cinema-graphite text-white"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Date + Method */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-cinema-muted/60 uppercase tracking-wider mb-1.5">Transaction Date</label>
                    <input
                      type="date"
                      required
                      value={formData.transaction_date}
                      onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                      className="input w-full rounded-none bg-cinema-graphite text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-cinema-muted/60 uppercase tracking-wider mb-1.5">Payment Method</label>
                    <select
                      value={formData.payment_method}
                      onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                      className="input w-full rounded-none bg-cinema-graphite text-white"
                    >
                      {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>
                </div>

                {/* Payer/Payee */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-cinema-muted/60 uppercase tracking-wider mb-1.5">Payer / Payee Name</label>
                    <input
                      type="text"
                      value={formData.payer_payee_name}
                      onChange={(e) => setFormData({ ...formData, payer_payee_name: e.target.value })}
                      className="input w-full rounded-none bg-cinema-graphite text-white"
                      placeholder="e.g. Speaker name, sponsor, vendor"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-cinema-muted/60 uppercase tracking-wider mb-1.5">Payer / Payee Contact</label>
                    <input
                      type="text"
                      value={formData.payer_payee_contact}
                      onChange={(e) => setFormData({ ...formData, payer_payee_contact: e.target.value })}
                      className="input w-full rounded-none bg-cinema-graphite text-white"
                      placeholder="Phone or email"
                    />
                  </div>
                </div>

                {/* Reference */}
                <div>
                  <label className="block text-xs text-cinema-muted/60 uppercase tracking-wider mb-1.5">Transaction Reference</label>
                  <input
                    type="text"
                    value={formData.transaction_reference}
                    onChange={(e) => setFormData({ ...formData, transaction_reference: e.target.value })}
                    className="input w-full rounded-none bg-cinema-graphite text-white"
                    placeholder="e.g. MM ref, cheque no., invoice #"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs text-cinema-muted/60 uppercase tracking-wider mb-1.5">Description / Notes</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="input w-full rounded-none bg-cinema-graphite text-white"
                    placeholder="What is this transaction for?"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="submit" className="cinema-btn-primary flex-1">
                    {editingId ? 'Update' : 'Record Transaction'}
                  </button>
                  <button type="button" onClick={() => { setShowModal(false); resetForm() }} className="cinema-btn-ghost">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

